/**
 * Wertia Dossier — Panels (8 sezioni in un solo file per praticità).
 *
 * Esporta:
 *   - OverviewPanel  (dashboard 6 card)
 *   - RedditoPanel
 *   - AvsLppPanel
 *   - ContrattiPanel
 *   - PatrimonioPanel
 *   - BudgetPanel
 *   - ConsulenzaPanel
 *   - DossierStartPanel (anagrafica + tipo consulenza)
 */
import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Plus, Trash2, Pencil, RefreshCw, Download, Edit3, Upload, Sparkles,
  Wallet, Shield, FileSignature, Building, Receipt, BarChart3,
  TrendingUp, AlertTriangle, Target, PiggyBank, Landmark, ChevronRight,
  Gem, GraduationCap, Home, User, Rocket, Crown, ArrowUpRight, Zap, CircleCheck,
  Bitcoin, CreditCard, Boxes, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { CHART_COLOR } from "@/lib/chartPalette";
import { fmtCHF, parseCHFInput, CONTRATTO_LABELS, CONTRATTO_TIPI_FORM, MODULI_BAROMETRO, API, authHeaders } from "./utils";
import { TimelineInvaliditaSVAG, TimelineDecessoSVAG, BarometerGauge, DonutDistribuzione, SchemaSistemaSvizzero, ComparativaScaleFiscali } from "./DossierCharts";
import axios from "axios";

/* ===========================================================================
 * SHARED UI
 * ======================================================================== */
const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{children}</h2>
    {action || null}
  </div>
);

const CardBox = ({ children, className = "", ...rest }) => (
  <div className={`rounded-lg border border-stone-200 bg-white p-4 ${className}`} {...rest}>
    {children}
  </div>
);

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">
      {label} {required ? <span className="text-red-500">*</span> : null}
    </Label>
    {children}
  </div>
);

const NumInput = ({ value, onChange, className = "", ...rest }) => {
  // Input "contabile": durante focus mostra il valore raw editabile,
  // su blur mostra formattazione svizzera "1'234.50". Sempre 2 decimali.
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const num = Number(value);
  const display = focused
    ? draft
    : (Number.isFinite(num) ? num.toLocaleString("it-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00");
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={(e) => {
        setFocused(true);
        setDraft(Number.isFinite(num) ? String(num) : "");
        setTimeout(() => e.target.select(), 0);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setFocused(false); onChange(parseCHFInput(draft)); }}
      className={`h-9 font-mono text-sm text-right ${className}`}
      {...rest}
    />
  );
};

/* ===========================================================================
 * DOSSIER START PANEL (Schermata 1 - anagrafica)
 * ======================================================================== */
export function DossierStartPanel({ dossier, updateSection, saving }) {
  const [basi, setBasi] = useState(dossier?.basi || {});
  const [persona, setPersona] = useState(dossier?.persone?.[0] || {});
  const [indirizzo, setIndirizzo] = useState(dossier?.indirizzo || {});
  const [resyncing, setResyncing] = React.useState(false);

  // Sincronizza i state locali quando il dossier ricarica (es. dopo resync)
  React.useEffect(() => {
    if (dossier?.basi) setBasi(dossier.basi);
    if (dossier?.persone?.[0]) setPersona(dossier.persone[0]);
    if (dossier?.indirizzo) setIndirizzo(dossier.indirizzo);
  }, [dossier?.id, dossier?.updated_at]);

  const save = async () => {
    await updateSection("basi", basi);
    await updateSection("persone", [persona]);
    await updateSection("indirizzo", indirizzo);
    toast.success("Dossier salvato");
  };

  const resyncFromClient = async () => {
    if (!dossier?.id) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/dossiers/${dossier.id}/resync-client`, {}, { headers: authHeaders() });
      const d = res.data || {};
      if (d.persone?.[0]) setPersona(d.persone[0]);
      if (d.indirizzo) setIndirizzo(d.indirizzo);
      const addr = d.indirizzo || {};
      const summary = [addr.via, addr.npa, addr.luogo, addr.cantone && `(${addr.cantone})`].filter(Boolean).join(" ");
      toast.success(`Dati sincronizzati dalla scheda cliente: ${summary || "completato"}`);
    } catch (err) {
      toast.error(`Sincronizzazione fallita: ${err.response?.data?.detail || err.message}`);
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dossier-start-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">Crea / Modifica Dossier</h1>
          <p className="text-sm text-stone-500 mt-1">Anagrafica auto-compilata dal cliente. Modifica se necessario.</p>
        </div>
        <Button
          variant="outline"
          onClick={resyncFromClient}
          disabled={resyncing || !dossier?.id}
          className="border-prime/40 text-prime hover:bg-prime/10 hover:text-prime"
          data-testid="d-resync-client"
          title="Ri-allinea il Dossier con i dati attuali della scheda cliente CRM (indirizzo, NPA, città, cantone, paese, figli)."
        >
          {resyncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {resyncing ? "Sincronizzo…" : "Sincronizza scheda cliente"}
        </Button>
      </div>

      <CardBox data-testid="dossier-basi-card">
        <SectionTitle>Basi di consulenza</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Situazione del">
            <Input type="date" value={basi.situazione_del || ""} onChange={(e) => setBasi({ ...basi, situazione_del: e.target.value })} className="h-9" data-testid="d-situazione-del" />
          </Field>
          <Field label="Consulente">
            <Input value={basi.consulente_email || ""} disabled className="h-9 bg-stone-50" />
          </Field>
          <Field label="Tipo consulenza">
            <Select value={basi.tipo_consulenza || "single"} onValueChange={(v) => setBasi({ ...basi, tipo_consulenza: v })}>
              <SelectTrigger className="h-9" data-testid="d-tipo-consulenza"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single — 1 persona</SelectItem>
                <SelectItem value="coppia">Coppia — 2 persone</SelectItem>
                <SelectItem value="coppia_figli">Coppia con figli</SelectItem>
                <SelectItem value="famiglia">Famiglia</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </CardBox>

      <CardBox data-testid="dossier-persona-card">
        <SectionTitle>Persona 1 (cliente principale)</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Cognome"><Input value={persona.cognome || ""} onChange={(e) => setPersona({ ...persona, cognome: e.target.value })} className="h-9" data-testid="d-cognome" /></Field>
          <Field label="Nome"><Input value={persona.nome || ""} onChange={(e) => setPersona({ ...persona, nome: e.target.value })} className="h-9" data-testid="d-nome" /></Field>
          <Field label="Sesso">
            <Select value={persona.sesso || "maschile"} onValueChange={(v) => setPersona({ ...persona, sesso: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="maschile">Maschile</SelectItem>
                <SelectItem value="femminile">Femminile</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Data di nascita"><Input type="date" value={persona.data_nascita || ""} onChange={(e) => setPersona({ ...persona, data_nascita: e.target.value })} className="h-9" data-testid="d-data-nascita" /></Field>
          <Field label="Stato civile">
            <Select value={persona.stato_civile || "celibe_nubile"} onValueChange={(v) => setPersona({ ...persona, stato_civile: v })}>
              <SelectTrigger className="h-9" data-testid="d-stato-civile"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="celibe_nubile">Celibe/Nubile</SelectItem>
                <SelectItem value="sposato">Sposato/a</SelectItem>
                <SelectItem value="divorziato">Divorziato/a</SelectItem>
                <SelectItem value="vedovo">Vedovo/a</SelectItem>
                <SelectItem value="separato">Separato/a</SelectItem>
                <SelectItem value="unione_registrata">Unione registrata</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Confessione">
            <Select value={persona.confessione || "senza_confessione"} onValueChange={(v) => setPersona({ ...persona, confessione: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="senza_confessione">Senza confessione</SelectItem>
                <SelectItem value="cattolica">Cattolica</SelectItem>
                <SelectItem value="evangelico_riformata">Evangelica-Riformata</SelectItem>
                <SelectItem value="ebraica">Ebraica</SelectItem>
                <SelectItem value="islamica">Islamica</SelectItem>
                <SelectItem value="altra">Altra</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Titolo accademico"><Input value={persona.titolo_accademico || ""} onChange={(e) => setPersona({ ...persona, titolo_accademico: e.target.value })} className="h-9" /></Field>
          <Field label="Lingua">
            <Select value={persona.lingua || "tedesco"} onValueChange={(v) => setPersona({ ...persona, lingua: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="italiano">Italiano</SelectItem>
                <SelectItem value="tedesco">Tedesco</SelectItem>
                <SelectItem value="francese">Francese</SelectItem>
                <SelectItem value="inglese">Inglese</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="ID esterno"><Input value={persona.id_esterno || ""} onChange={(e) => setPersona({ ...persona, id_esterno: e.target.value })} className="h-9" /></Field>
        </div>
      </CardBox>

      <CardBox data-testid="dossier-indirizzo-card">
        <SectionTitle>Indirizzo</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Via"><Input value={indirizzo.via || ""} onChange={(e) => setIndirizzo({ ...indirizzo, via: e.target.value })} className="h-9" data-testid="d-via" /></Field>
          <Field label="NPA / luogo">
            <div className="grid grid-cols-2 gap-2">
              <Input value={indirizzo.npa || ""} onChange={(e) => setIndirizzo({ ...indirizzo, npa: e.target.value })} className="h-9" placeholder="NPA" data-testid="d-npa" />
              <Input value={indirizzo.luogo || ""} onChange={(e) => setIndirizzo({ ...indirizzo, luogo: e.target.value })} className="h-9" placeholder="Luogo" data-testid="d-luogo" />
            </div>
          </Field>
          <Field label="Cantone">
            <Select
              value={indirizzo.cantone || "ZH"}
              onValueChange={(v) => setIndirizzo({ ...indirizzo, cantone: v })}
            >
              <SelectTrigger className="h-9" data-testid="d-cantone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["ZH","BE","LU","UR","SZ","OW","NW","GL","ZG","FR","SO","BS","BL","SH","AR","AI","SG","GR","AG","TG","TI","VD","VS","NE","GE","JU"].map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Paese"><Input value={indirizzo.paese || "CH"} onChange={(e) => setIndirizzo({ ...indirizzo, paese: e.target.value.toUpperCase() })} className="h-9" data-testid="d-paese" /></Field>
        </div>
      </CardBox>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-start">
          {saving ? "Salvataggio..." : "Salva dossier"}
        </Button>
      </div>
    </div>
  );
}

/* ===========================================================================
 * OVERVIEW PANEL (Schermata 3 - 6 card dashboard)
 * ======================================================================== */
export function OverviewPanel({ dossier, computed }) {
  const c = computed || {};
  const speseAss = (dossier?.budget?.spese || []).filter((s) => s.categoria === "assicurazioni").reduce((s, v) => s + Number(v.importo_chf || 0), 0);
  const cards = [
    { titolo: "REDDITO", sotto: "Reddito lordo annuo", valore: fmtCHF(c.reddito?.lordo_annuo || 0), icon: Wallet, color: "border-emerald-500/60" },
    { titolo: "AVS E LPP", sotto: "Previdenza annua", valore: fmtCHF((c.avs?.rendita_vecchiaia_annua || 0) + (c.lpp?.rendita_vecchiaia_annua || 0)), icon: Shield, color: "border-blue-500/60" },
    { titolo: "CONTRATTI", sotto: `${(dossier?.contratti || []).length} contratti · spese annue`, valore: fmtCHF(speseAss), icon: FileSignature, color: "border-amber-500/60" },
    { titolo: "POLIZZE DI PREVIDENZA", sotto: "Capitale LPP", valore: fmtCHF((c.lpp?.capitale_attuale || 0)), icon: Receipt, color: "border-purple-500/60" },
    { titolo: "PATRIMONIO", sotto: "Patrimonio netto", valore: fmtCHF(c.patrimonio?.totale_netto || 0), icon: Building, color: "border-indigo-500/60" },
    { titolo: "BUDGET", sotto: "Quota di risparmio", valore: fmtCHF(c.budget?.quota_risparmio || 0), icon: BarChart3, color: "border-rose-500/60" },
  ];

  return (
    <div className="space-y-6" data-testid="dossier-overview-panel">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-stone-900">Profilo del cliente</h1>
        <p className="text-sm text-stone-500 mt-1">Panoramica dei dati Finanz-Analyse. Tutti i KPI sono auto-calcolati.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <CardBox key={i} className={`border-l-4 ${c.color} hover:shadow-md transition`} data-testid={`d-overview-card-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{c.titolo}</div>
                  <div className="text-xs text-stone-400 mt-0.5">{c.sotto}</div>
                  <div className="text-xl font-mono font-semibold text-stone-900 mt-3">{c.valore}</div>
                </div>
                <Icon className="w-5 h-5 text-stone-400" />
              </div>
            </CardBox>
          );
        })}
      </div>
    </div>
  );
}

/* ===========================================================================
 * REDDITO PANEL (Schermata 4)
 * ======================================================================== */
export function RedditoPanel({ dossier, computed, updateSection, saving }) {
  const [reddito, setReddito] = useState(dossier?.reddito || {});
  const gp = reddito.guadagno_principale || {};
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);
  // Auto-save state — evita di salvare al mount + debounce
  const initialSnapshotRef = useRef(JSON.stringify(dossier?.reddito || {}));
  const lastSavedSnapshotRef = useRef(JSON.stringify(dossier?.reddito || {}));
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | pending | saved

  const setGP = (patch) => setReddito({ ...reddito, guadagno_principale: { ...gp, ...patch } });

  const save = async () => {
    await updateSection("reddito", reddito);
    lastSavedSnapshotRef.current = JSON.stringify(reddito);
    setAutoSaveState("saved");
    toast.success("Reddito salvato");
  };

  // ─── Auto-save con debounce (800ms) — ricalcola live ──────────────────
  useEffect(() => {
    const currentSnap = JSON.stringify(reddito);
    // Skip al primo render (stato iniziale)
    if (currentSnap === initialSnapshotRef.current) return;
    // Skip se già salvato
    if (currentSnap === lastSavedSnapshotRef.current) return;
    setAutoSaveState("pending");
    const t = setTimeout(async () => {
      try {
        await updateSection("reddito", reddito);
        lastSavedSnapshotRef.current = currentSnap;
        setAutoSaveState("saved");
        // reset badge "saved" dopo 2s
        setTimeout(() => setAutoSaveState("idle"), 2000);
      } catch (e) {
        setAutoSaveState("idle");
      }
    }, 800);
    return () => clearTimeout(t);
  }, [reddito, updateSection]);

  // ─── Auto-sync netto: quando il backend calcola un nuovo netto, salva il valore
  //     in gp.reddito_netto_annuale così viene persistito in DB e usato dai PDF/Excel.
  useEffect(() => {
    const calcNet = computed?.reddito?.netto_annuo;
    if (calcNet == null) return;
    if (Math.abs((gp.reddito_netto_annuale || 0) - calcNet) < 0.5) return;  // già allineato
    setGP({ reddito_netto_annuale: Math.round(calcNet * 100) / 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed?.reddito?.netto_annuo]);

  // ─── AI extraction cedolino (Lohnabrechnung / Provisionsabrechnung) ─────
  const onCedolinoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !dossier?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("provider", "claude");
      const res = await axios.post(
        `${API}/dossiers/${dossier.id}/extract-cedolino`,
        fd,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
      );
      const fields = res.data?.fields || {};
      // Merge nei campi locali (il consulente può ancora editare prima di Salvare)
      setReddito((prev) => ({
        ...prev,
        guadagno_principale: { ...(prev.guadagno_principale || {}), ...fields },
      }));
      toast.success(`Cedolino estratto: lordo CHF ${(fields.reddito_lordo_annuale || 0).toLocaleString("de-CH")} / anno. Verifica i valori e clicca Salva.`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Estrazione cedolino fallita: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addAccessorio = () => setReddito({ ...reddito, accessori: [...(reddito.accessori || []), { titolo: "", importo_annuale: 0 }] });
  const addSostitutivo = (tipo) => setReddito({
    ...reddito, sostitutivi: [...(reddito.sostitutivi || []), {
      tipo, titolo: tipo === "decesso" ? "AVS - Rendita per vedove/i" : tipo === "pensionamento" ? "AVS - Rendita di vecchiaia" : "AI - Rendita d'invalidità",
      non_riceve_rendita: false, rendita_avs_attuale_chf: 0, rendita_avs_dal: "",
    }],
  });
  const addAltra = () => setReddito({ ...reddito, altre_entrate: [...(reddito.altre_entrate || []), { titolo: "", importo_annuale: 0 }] });

  return (
    <div className="space-y-6" data-testid="dossier-reddito-panel">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">Reddito</h1>
          <p className="text-sm text-stone-500 mt-1">Attività principale + redditi accessori e sostitutivi.</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={onCedolinoSelected}
            accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
            className="hidden"
            data-testid="d-cedolino-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !dossier?.id}
            variant="outline"
            className="border-prime/40 text-prime hover:bg-prime/10 hover:text-prime"
            data-testid="d-cedolino-upload-btn"
            title="Carica una Lohnabrechnung / Lohnausweis / Provisionsabrechnung — l'AI compila in automatico tutti i campi (lordo, netto, AHV, ALV, NBU, UVGZ, KTG, Quellensteuer, ecc.)."
          >
            {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {uploading ? "Estrazione…" : "Carica cedolino (AI)"}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-reddito">Salva</Button>
        </div>
      </div>

      <CardBox data-testid="d-reddito-principale">
        <SectionTitle>Attività principale</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Tipo">
            <Select value={gp.tipo || "dipendente"} onValueChange={(v) => setGP({ tipo: v })}>
              <SelectTrigger className="h-9" data-testid="d-tipo-reddito"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dipendente">Dipendente</SelectItem>
                <SelectItem value="indipendente">Indipendente</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Professione"><Input value={gp.professione || ""} onChange={(e) => setGP({ professione: e.target.value })} className="h-9" /></Field>
          <Field label="Datore di lavoro"><Input value={gp.datore_lavoro || ""} onChange={(e) => setGP({ datore_lavoro: e.target.value })} className="h-9" /></Field>
          <Field label="Grado di occupazione (%)"><NumInput value={gp.grado_occupazione || 100} onChange={(v) => setGP({ grado_occupazione: v })} /></Field>
          <Field label="Impiegato/a dal"><Input type="date" value={gp.impiegato_dal || ""} onChange={(e) => setGP({ impiegato_dal: e.target.value })} className="h-9" /></Field>
          <Field label="NPA / Luogo">
            <div className="grid grid-cols-2 gap-2">
              <Input value={gp.npa || ""} onChange={(e) => setGP({ npa: e.target.value })} className="h-9" placeholder="NPA" />
              <Input value={gp.luogo || ""} onChange={(e) => setGP({ luogo: e.target.value })} className="h-9" placeholder="Luogo" />
            </div>
          </Field>
          <Field label="">
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <Checkbox checked={!!gp.con_imposta_alla_fonte} onCheckedChange={(v) => setGP({ con_imposta_alla_fonte: !!v })} />
              <span className="text-xs">Con imposta alla fonte</span>
            </label>
          </Field>
          {gp.tipo === "indipendente" && (
            <Field label="">
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <Checkbox checked={!!gp.assicurato_lainf} onCheckedChange={(v) => setGP({ assicurato_lainf: !!v })} />
                <span className="text-xs">Assicurato LAINF</span>
              </label>
            </Field>
          )}
        </div>

        {/* ── REDDITO LORDO + NETTO + CALCOLO AUTOMATICO (raggruppati) ── */}
        <div className="mt-6 rounded-lg border-2 border-prime/40 bg-prime/5 p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-serif font-semibold text-stone-900">Reddito & Calcolo Automatico</span>
              {autoSaveState === "pending" && (
                <span className="text-[11px] text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Aggiornamento…</span>
              )}
              {autoSaveState === "saved" && (
                <span className="text-[11px] text-emerald-600 font-medium">✓ Aggiornato</span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={save}
              disabled={saving}
              className="h-8 px-3 border-prime/40 text-prime hover:bg-prime/10 hover:text-prime"
              data-testid="d-refresh-reddito"
              title="Forza il salvataggio + ricalcolo immediato (di default l'auto-save parte dopo 800ms)"
            >
              {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              {saving ? "Calcolo…" : "Aggiorna ora"}
            </Button>
          </div>

          {/* Input Lordo + Output Netto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Reddito lordo annuale (CHF)" required>
              <NumInput value={gp.reddito_lordo_annuale || 0} onChange={(v) => setGP({ reddito_lordo_annuale: v })} data-testid="d-reddito-lordo" />
            </Field>
            <Field label="Reddito netto annuale calcolato (CHF)">
              <div className="relative">
                <NumInput
                  value={computed?.reddito?.netto_annuo ?? gp.reddito_netto_annuale ?? 0}
                  onChange={() => { /* read-only: il netto è output del calcolo */ }}
                  data-testid="d-reddito-netto"
                  disabled
                  className="bg-stone-100 cursor-not-allowed text-prime font-semibold"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-prime/60 uppercase tracking-wide pointer-events-none">auto</span>
              </div>
            </Field>
          </div>

          {/* Breakdown KPI */}
          {computed?.reddito ? (
            <div className="rounded border border-prime/30 bg-white p-3 text-xs space-y-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono">
                <div><span className="text-stone-500">Netto calc.:</span> <span className="text-prime font-semibold">{fmtCHF(computed.reddito.netto_annuo)}</span></div>
                <div><span className="text-stone-500">AVS/IV/IPG:</span> {fmtCHF(computed.reddito.ahv_iv_eo)}</div>
                <div><span className="text-stone-500">ALV:</span> {fmtCHF(computed.reddito.alv)}</div>
                <div><span className="text-stone-500">NBU:</span> {fmtCHF(computed.reddito.nbu)}</div>
                <div>
                  <span className="text-stone-500">LPP:</span> {fmtCHF(computed.reddito.bvg)}
                  {computed.reddito.bvg_rate > 0 && (
                    <span className="text-stone-400 ml-1">({(computed.reddito.bvg_rate * 100).toFixed(0)}%)</span>
                  )}
                </div>
              </div>
              {(computed.reddito.uvgz > 0 || computed.reddito.ktg > 0 || computed.reddito.quellensteuer > 0 || computed.reddito.altre_deduzioni > 0) ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono pt-2 border-t border-prime/20">
                  <div><span className="text-stone-500">Ded. totali:</span> <span className="text-stone-900 font-semibold">{fmtCHF(computed.reddito.deduzioni_totali || computed.reddito.deduzioni_sociali)}</span></div>
                  {computed.reddito.uvgz > 0 && <div><span className="text-stone-500">UVGZ:</span> {fmtCHF(computed.reddito.uvgz)}</div>}
                  {computed.reddito.ktg > 0 && <div><span className="text-stone-500">KTG:</span> {fmtCHF(computed.reddito.ktg)}</div>}
                  {computed.reddito.quellensteuer > 0 && (
                    <>
                      <div><span className="text-stone-500">Quellensteuer:</span> {fmtCHF(computed.reddito.quellensteuer)}</div>
                      <div><span className="text-stone-500">Quellensteuer/monate:</span> {fmtCHF(computed.reddito.quellensteuer / 12)}</div>
                    </>
                  )}
                  {computed.reddito.altre_deduzioni > 0 && <div><span className="text-stone-500">Altre:</span> {fmtCHF(computed.reddito.altre_deduzioni)}</div>}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-[11px] text-stone-500 italic">Inserisci il reddito lordo: il netto e le deduzioni si calcoleranno automaticamente.</p>
          )}
        </div>
      </CardBox>

      {/* Deduzioni aggiuntive dal cedolino (UVGZ, KTG, Quellensteuer, altre) */}
      <CardBox data-testid="d-reddito-deduzioni-aggiuntive">
        <SectionTitle>Deduzioni aggiuntive dal cedolino (annue)</SectionTitle>
        <p className="text-[11px] text-stone-500 mb-3 leading-snug">
          Voci non incluse nel calcolo standard AHV/ALV/NBU/LPP. Compila se presenti sul cedolino del cliente
          (es. Lohnausweis o Provisionsabrechnung). Tutti i valori sono <span className="font-semibold">CHF/anno</span>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="UVGZ — NBU complementare">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).uvgz_annuo || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), uvgz_annuo: v } })}
              data-testid="d-ded-uvgz"
            />
          </Field>
          <Field label="KTG — Krankentaggeld (Summen + Schaden)">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).ktg_annuo || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), ktg_annuo: v } })}
              data-testid="d-ded-ktg"
            />
          </Field>
          <Field label="Quellensteuer (ritenuta alla fonte)">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).quellensteuer_annuo || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), quellensteuer_annuo: v } })}
              data-testid="d-ded-qst"
              disabled={!gp.con_imposta_alla_fonte}
            />
            <span className="text-[10px] text-stone-400 italic">
              {gp.con_imposta_alla_fonte
                ? "Lascia 0 per stima automatica (10% del lordo)"
                : "Attiva «Con imposta alla fonte» per applicarla"}
            </span>
          </Field>
          <Field label="PK/BVG override (valore reale dal cedolino)">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).bvg_override_annuo || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), bvg_override_annuo: v } })}
              data-testid="d-ded-bvg-override"
            />
            <span className="text-[10px] text-stone-400 italic">Lascia 0 per usare la stima automatica</span>
          </Field>
          <Field label="NBU/UVG override (valore reale dal cedolino)">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).nbu_override_annuo || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), nbu_override_annuo: v } })}
              data-testid="d-ded-nbu-override"
            />
            <span className="text-[10px] text-stone-400 italic">Lascia 0 per usare la stima automatica</span>
          </Field>
          <Field label="Altre deduzioni (IT-Package, corsi, ecc.)">
            <NumInput
              value={(gp.deduzioni_aggiuntive || {}).altre_deduzioni_annue || 0}
              onChange={(v) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), altre_deduzioni_annue: v } })}
              data-testid="d-ded-altre"
            />
          </Field>
          <Field label="Descrizione altre deduzioni">
            <Input
              value={(gp.deduzioni_aggiuntive || {}).altre_deduzioni_titolo || ""}
              onChange={(e) => setGP({ deduzioni_aggiuntive: { ...(gp.deduzioni_aggiuntive || {}), altre_deduzioni_titolo: e.target.value } })}
              className="h-9"
              placeholder="es. IT-Package + VBV/IAF-Kursgebühren"
              data-testid="d-ded-altre-titolo"
            />
          </Field>
        </div>
      </CardBox>

      {/* Sostitutivi */}
      <CardBox data-testid="d-reddito-sostitutivi">
        <SectionTitle action={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => addSostitutivo("decesso")} data-testid="d-add-decesso"><Plus className="w-3 h-3 mr-1" />Decesso</Button>
            <Button size="sm" variant="outline" onClick={() => addSostitutivo("pensionamento")} data-testid="d-add-pensionamento"><Plus className="w-3 h-3 mr-1" />Pensionamento</Button>
            <Button size="sm" variant="outline" onClick={() => addSostitutivo("incapacita_guadagno")} data-testid="d-add-invalidita"><Plus className="w-3 h-3 mr-1" />Incapacità di guadagno</Button>
          </div>
        }>Reddito sostitutivo</SectionTitle>
        {(reddito.sostitutivi || []).length === 0 ? (
          <p className="text-sm text-stone-400 italic">Nessun reddito sostitutivo registrato</p>
        ) : (
          <div className="space-y-3">
            {(reddito.sostitutivi || []).map((s, i) => (
              <div key={i} className="rounded border p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                <Field label="Tipo"><div className="text-xs font-medium uppercase">{s.tipo}</div></Field>
                <Field label="Titolo"><Input value={s.titolo || ""} onChange={(e) => {
                  const arr = [...reddito.sostitutivi]; arr[i] = { ...s, titolo: e.target.value }; setReddito({ ...reddito, sostitutivi: arr });
                }} className="h-9" /></Field>
                <Field label="Rendita attuale (CHF/anno)">
                  <NumInput value={s.rendita_avs_attuale_chf || 0} onChange={(v) => {
                    const arr = [...reddito.sostitutivi]; arr[i] = { ...s, rendita_avs_attuale_chf: v }; setReddito({ ...reddito, sostitutivi: arr });
                  }} />
                </Field>
                <Button size="sm" variant="ghost" onClick={() => {
                  const arr = reddito.sostitutivi.filter((_, j) => j !== i); setReddito({ ...reddito, sostitutivi: arr });
                }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardBox>

      {/* Accessori + Altre entrate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardBox>
          <SectionTitle action={<Button size="sm" variant="outline" onClick={addAccessorio}><Plus className="w-3 h-3 mr-1" />Aggiungi</Button>}>Reddito accessorio</SectionTitle>
          {(reddito.accessori || []).length === 0 ? <p className="text-sm text-stone-400 italic">Nessun reddito accessorio</p> :
            <div className="space-y-2">{(reddito.accessori || []).map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                <Input value={a.titolo || ""} placeholder="Designazione" onChange={(e) => { const arr = [...reddito.accessori]; arr[i] = { ...a, titolo: e.target.value }; setReddito({ ...reddito, accessori: arr }); }} className="h-8 text-xs" />
                <NumInput value={a.importo_annuale} onChange={(v) => { const arr = [...reddito.accessori]; arr[i] = { ...a, importo_annuale: v }; setReddito({ ...reddito, accessori: arr }); }} className="w-28 h-8 text-xs" />
                <Button size="sm" variant="ghost" onClick={() => setReddito({ ...reddito, accessori: reddito.accessori.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}</div>}
        </CardBox>
        <CardBox>
          <SectionTitle action={<Button size="sm" variant="outline" onClick={addAltra}><Plus className="w-3 h-3 mr-1" />Aggiungi</Button>}>Altre entrate</SectionTitle>
          {(reddito.altre_entrate || []).length === 0 ? <p className="text-sm text-stone-400 italic">Nessun'altra entrata</p> :
            <div className="space-y-2">{(reddito.altre_entrate || []).map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                <Input value={a.titolo || ""} placeholder="Designazione" onChange={(e) => { const arr = [...reddito.altre_entrate]; arr[i] = { ...a, titolo: e.target.value }; setReddito({ ...reddito, altre_entrate: arr }); }} className="h-8 text-xs" />
                <NumInput value={a.importo_annuale} onChange={(v) => { const arr = [...reddito.altre_entrate]; arr[i] = { ...a, importo_annuale: v }; setReddito({ ...reddito, altre_entrate: arr }); }} className="w-28 h-8 text-xs" />
                <Button size="sm" variant="ghost" onClick={() => setReddito({ ...reddito, altre_entrate: reddito.altre_entrate.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}</div>}
        </CardBox>
      </div>
    </div>
  );
}

/* ===========================================================================
 * AVS E LPP PANEL (Schermata 6)
 * ======================================================================== */
export function AvsLppPanel({ dossier, computed, updateSection, saving }) {
  const [avsLpp, setAvsLpp] = useState(dossier?.avs_lpp || {});
  const avs = avsLpp.avs || {};
  const lpp = avsLpp.lpp_base || {};
  // Auto-save
  const initialSnapAvs = useRef(JSON.stringify(dossier?.avs_lpp || {}));
  const lastSavedSnapAvs = useRef(JSON.stringify(dossier?.avs_lpp || {}));
  const [avsSaveState, setAvsSaveState] = useState("idle");

  const setAvs = (patch) => setAvsLpp({ ...avsLpp, avs: { ...avs, ...patch } });
  const setLpp = (patch) => setAvsLpp({ ...avsLpp, lpp_base: { ...lpp, ...patch } });

  const save = async () => {
    await updateSection("avs_lpp", avsLpp);
    lastSavedSnapAvs.current = JSON.stringify(avsLpp);
    setAvsSaveState("saved");
    toast.success("AVS e LPP salvati");
  };

  // Auto-save debounced
  useEffect(() => {
    const cur = JSON.stringify(avsLpp);
    if (cur === initialSnapAvs.current) return;
    if (cur === lastSavedSnapAvs.current) return;
    setAvsSaveState("pending");
    const t = setTimeout(async () => {
      try {
        await updateSection("avs_lpp", avsLpp);
        lastSavedSnapAvs.current = cur;
        setAvsSaveState("saved");
        setTimeout(() => setAvsSaveState("idle"), 2000);
      } catch (e) { setAvsSaveState("idle"); }
    }, 800);
    return () => clearTimeout(t);
  }, [avsLpp, updateSection]);

  const addStep = () => setAvs({ step_redditi: [...(avs.step_redditi || []), { eta_da: 21, eta_a: 65, reddito_annuale: 0 }] });

  return (
    <div className="space-y-6" data-testid="dossier-avs-lpp-panel">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">AVS e LPP</h1>
          <p className="text-sm text-stone-500 mt-1">Calcoli automatici Scala 44 + LPP (Umwandlungssatz 6.8%).</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-avs-lpp">Salva</Button>
      </div>

      <CardBox data-testid="d-avs-card">
        <SectionTitle>AVS — Evoluzione salariale</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Field label="">
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <Checkbox checked={avs.ass_avs_dall_eta_21 !== false} onCheckedChange={(v) => setAvs({ ass_avs_dall_eta_21: !!v })} />
              <span className="text-xs">Ass. AVS dall'età di 21 anni</span>
            </label>
          </Field>
          {avs.ass_avs_dall_eta_21 === false && (
            <Field label="In CH da (anno)"><Input value={avs.in_ch_da || ""} onChange={(e) => setAvs({ in_ch_da: e.target.value })} className="h-9" placeholder="es. 2015" /></Field>
          )}
          <Field label="Base AVS">
            <Select value={avs.base_avs || "evoluzione_salariale"} onValueChange={(v) => setAvs({ base_avs: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="evoluzione_salariale">Evoluzione salariale</SelectItem>
                <SelectItem value="ultimo_reddito">Ultimo reddito</SelectItem>
                <SelectItem value="reddito_fisso">Reddito fisso</SelectItem>
                <SelectItem value="media_carriera">Media carriera</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="border rounded">
          <div className="bg-stone-50 px-3 py-2 text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center justify-between">
            <span>Step redditi (carriera)</span>
            <Button size="sm" variant="ghost" onClick={addStep}><Plus className="w-3 h-3 mr-1" />Aggiungi step</Button>
          </div>
          <div className="p-3 space-y-2">
            {(avs.step_redditi || []).map((s, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-center">
                <Field label="Età da"><Input type="number" value={s.eta_da} onChange={(e) => { const arr = [...avs.step_redditi]; arr[i] = { ...s, eta_da: Number(e.target.value) }; setAvs({ step_redditi: arr }); }} className="h-8 text-xs" /></Field>
                <Field label="Età a"><Input type="number" value={s.eta_a} onChange={(e) => { const arr = [...avs.step_redditi]; arr[i] = { ...s, eta_a: Number(e.target.value) }; setAvs({ step_redditi: arr }); }} className="h-8 text-xs" /></Field>
                <Field label="Reddito annuale (CHF)"><NumInput value={s.reddito_annuale} onChange={(v) => { const arr = [...avs.step_redditi]; arr[i] = { ...s, reddito_annuale: v }; setAvs({ step_redditi: arr }); }} /></Field>
                <Button size="sm" variant="ghost" onClick={() => setAvs({ step_redditi: avs.step_redditi.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            ))}
            {(avs.step_redditi || []).length === 0 && <p className="text-xs text-stone-400 italic">Nessun step. Aggiungi per calcolare la rendita.</p>}
          </div>
        </div>

        <div className="mt-4 rounded border border-prime/30 bg-prime/5 p-3 text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-prime/80">Calcolo AVS</span>
              {avsSaveState === "pending" && (
                <span className="text-[10px] text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Aggiornamento…</span>
              )}
              {avsSaveState === "saved" && (
                <span className="text-[10px] text-emerald-600 font-medium">✓ Aggiornato</span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={save}
              disabled={saving}
              className="h-7 px-3 border-prime/40 text-prime hover:bg-prime/10 hover:text-prime"
              data-testid="d-refresh-avs"
              title="Forza il salvataggio + ricalcolo immediato"
            >
              {saving ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
              {saving ? "Calcolo…" : "Aggiorna ora"}
            </Button>
          </div>
          {computed?.avs ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono">
              <div><span className="text-stone-500">Media determ.:</span> <span className="font-semibold">{fmtCHF(computed.avs.media_determinante)}</span></div>
              <div><span className="text-stone-500">Rendita annua:</span> <span className="text-prime font-semibold">{fmtCHF(computed.avs.rendita_vecchiaia_annua)}</span></div>
              <div><span className="text-stone-500">Anni mancanti:</span> {computed.avs.anni_mancanti}</div>
              <div><span className="text-stone-500">Riduzione:</span> {computed.avs.riduzione_per_lacuna_percent}%</div>
            </div>
          ) : (
            <p className="text-[11px] text-stone-500 italic">Clicca <b>Aggiorna</b> per ricalcolare la rendita AVS in base agli step inseriti.</p>
          )}
        </div>
      </CardBox>

      <CardBox data-testid="d-lpp-card">
        <SectionTitle>LPP — Reddito di base</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Istituto di previdenza"><Input value={lpp.istituto_previdenza || ""} onChange={(e) => setLpp({ istituto_previdenza: e.target.value })} className="h-9" /></Field>
          <Field label="Numero contratto"><Input value={lpp.numero_contratto || ""} onChange={(e) => setLpp({ numero_contratto: e.target.value })} className="h-9" /></Field>
          <Field label="Capitale copertura LPP (CHF)"><NumInput value={lpp.capitale_copertura_lpp_chf || 0} onChange={(v) => setLpp({ capitale_copertura_lpp_chf: v })} data-testid="d-lpp-capitale" /></Field>
          <Field label="Prelievo PPA (CHF)"><NumInput value={lpp.prelievo_ppa_chf || 0} onChange={(v) => setLpp({ prelievo_ppa_chf: v })} /></Field>
          <Field label="Imposte sul capitale (CHF)"><NumInput value={lpp.imposte_capitale_chf || 0} onChange={(v) => setLpp({ imposte_capitale_chf: v })} /></Field>
          <Field label="Limite prest. IG (%)"><NumInput value={lpp.limite_prestazione_ig || 90} onChange={(v) => setLpp({ limite_prestazione_ig: v })} /></Field>
        </div>

        <div className="mt-4 rounded border border-prime/30 bg-prime/5 p-3 text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-prime/80">Calcolo LPP</span>
              {avsSaveState === "pending" && (
                <span className="text-[10px] text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Aggiornamento…</span>
              )}
              {avsSaveState === "saved" && (
                <span className="text-[10px] text-emerald-600 font-medium">✓ Aggiornato</span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={save}
              disabled={saving}
              className="h-7 px-3 border-prime/40 text-prime hover:bg-prime/10 hover:text-prime"
              data-testid="d-refresh-lpp"
              title="Forza il salvataggio + ricalcolo immediato"
            >
              {saving ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
              {saving ? "Calcolo…" : "Aggiorna ora"}
            </Button>
          </div>
          {computed?.lpp ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono">
              <div><span className="text-stone-500">Cap. attuale:</span> {fmtCHF(computed.lpp.capitale_attuale)}</div>
              <div><span className="text-stone-500">Cap. proj. 65:</span> <span className="font-semibold">{fmtCHF(computed.lpp.capitale_proiettato_a_65)}</span></div>
              <div><span className="text-stone-500">Sal. coord.:</span> {fmtCHF(computed.lpp.salario_coordinato)}</div>
              <div><span className="text-stone-500">Rendita annua:</span> <span className="text-prime font-semibold">{fmtCHF(computed.lpp.rendita_vecchiaia_annua)}</span></div>
            </div>
          ) : (
            <p className="text-[11px] text-stone-500 italic">Clicca <b>Aggiorna</b> per ricalcolare la rendita LPP in base ai dati inseriti.</p>
          )}
        </div>
      </CardBox>
    </div>
  );
}

/* ===========================================================================
 * CONTRATTI PANEL (Schermata 7)
 * ======================================================================== */
export function ContrattiPanel({ dossier, updateSection, reimportContratti, saving }) {
  const [contratti, setContratti] = useState(dossier?.contratti || []);
  const [adding, setAdding] = useState(false);
  const [newTipo, setNewTipo] = useState("registrazione_libera");

  const save = async () => {
    await updateSection("contratti", contratti);
    toast.success("Contratti salvati");
  };

  const addManual = () => {
    setContratti([
      ...contratti,
      {
        id: `manual-${Date.now()}`,
        tipo: newTipo,
        titolo: CONTRATTO_LABELS[newTipo] || newTipo,
        compagnia: "",
        numero_polizza: "",
        intestatario: "",
        data_inizio: "",
        data_fine: "",
        premio_mensile_chf: 0,
        premio_annuo_chf: 0,
        source: "manual_external",
        attivo: true,
        extra: {},
      },
    ]);
    setAdding(false);
  };

  return (
    <div className="space-y-6" data-testid="dossier-contratti-panel">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">Contratti</h1>
          <p className="text-sm text-stone-500 mt-1">
            {contratti.filter((c) => c.source !== "manual_external").length} importati da Wertia · {contratti.filter((c) => c.source === "manual_external").length} esterni
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={reimportContratti} disabled={saving} data-testid="d-reimport-contratti"><RefreshCw className="w-3 h-3 mr-1" />Re-importa Wertia</Button>
          <Button size="sm" onClick={() => setAdding(!adding)} data-testid="d-add-contratto"><Plus className="w-3 h-3 mr-1" />Nuovo contratto esterno</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-contratti">Salva</Button>
        </div>
      </div>

      {adding && (
        <CardBox className="border-prime">
          <SectionTitle>Nuovo contratto esterno</SectionTitle>
          <div className="flex gap-2 items-end">
            <Field label="Tipo">
              <Select value={newTipo} onValueChange={setNewTipo}>
                <SelectTrigger className="h-9 w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTRATTO_TIPI_FORM.map((t) => <SelectItem key={t} value={t}>{CONTRATTO_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Button onClick={addManual} className="bg-prime text-prime-foreground" data-testid="d-confirm-add-contratto">Aggiungi</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Annulla</Button>
          </div>
        </CardBox>
      )}

      {contratti.length === 0 ? (
        <CardBox><p className="text-sm text-stone-400 italic">Nessun contratto registrato</p></CardBox>
      ) : (
        <div className="space-y-3">
          {contratti.map((c, i) => (
            <CardBox key={c.id || i} className="border-l-4 border-stone-300" data-testid={`d-contratto-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">Tipo</div>
                    <div className="font-semibold">{CONTRATTO_LABELS[c.tipo] || c.tipo}</div>
                    <Badge variant={c.source === "manual_external" ? "outline" : "default"} className="mt-1 text-[9px]">
                      {c.source === "wertia_policy" ? "Polizza Wertia" : c.source === "wertia_antrag" ? "Antrag Wertia" : "Esterno"}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">Compagnia</div>
                    <Input value={c.compagnia || ""} onChange={(e) => { const arr = [...contratti]; arr[i] = { ...c, compagnia: e.target.value }; setContratti(arr); }} className="h-8 text-sm" disabled={c.source !== "manual_external"} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">N. polizza</div>
                    <Input value={c.numero_polizza || ""} onChange={(e) => { const arr = [...contratti]; arr[i] = { ...c, numero_polizza: e.target.value }; setContratti(arr); }} className="h-8 text-sm" disabled={c.source !== "manual_external"} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">Premio annuo</div>
                    <NumInput value={c.premio_annuo_chf} onChange={(v) => { const arr = [...contratti]; arr[i] = { ...c, premio_annuo_chf: v }; setContratti(arr); }} />
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setContratti(contratti.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </CardBox>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===========================================================================
 * PATRIMONIO PANEL (Schermata 8)
 * ======================================================================== */
export function PatrimonioPanel({ dossier, computed, updateSection, saving }) {
  const [patr, setPatr] = useState(dossier?.patrimonio || {});
  const initialSnapPatr = useRef(JSON.stringify(dossier?.patrimonio || {}));
  const lastSavedPatr = useRef(JSON.stringify(dossier?.patrimonio || {}));
  const [patrSaveState, setPatrSaveState] = useState("idle");

  const save = async () => {
    await updateSection("patrimonio", patr);
    lastSavedPatr.current = JSON.stringify(patr);
    setPatrSaveState("saved");
    toast.success("Patrimonio salvato");
  };

  // Auto-save debounced (800ms)
  useEffect(() => {
    const cur = JSON.stringify(patr);
    if (cur === initialSnapPatr.current) return;
    if (cur === lastSavedPatr.current) return;
    setPatrSaveState("pending");
    const t = setTimeout(async () => {
      try {
        await updateSection("patrimonio", patr);
        lastSavedPatr.current = cur;
        setPatrSaveState("saved");
        setTimeout(() => setPatrSaveState("idle"), 2000);
      } catch (e) { setPatrSaveState("idle"); }
    }, 800);
    return () => clearTimeout(t);
  }, [patr, updateSection]);

  // 8 categorie patrimoniali con icone Lucide moderne + tono colorato per categoria
  const attivi = [
    { key: "liquidita",   titolo: "Liquidità",       icon: Wallet,     tone: "sky" },
    { key: "titoli",      titolo: "Titoli",          icon: TrendingUp, tone: "emerald" },
    { key: "fondi",       titolo: "Fondi",           icon: Landmark,   tone: "indigo" },
    { key: "crypto",      titolo: "Crypto",          icon: Bitcoin,    tone: "orange" },
    { key: "immobili",    titolo: "Immobili",        icon: Home,       tone: "purple" },
    { key: "altri_averi", titolo: "Altri averi",     icon: Boxes,      tone: "stone" },
  ];
  const passivi = [
    { key: "ipoteche", titolo: "Ipoteche",         icon: Building,    tone: "rose" },
    { key: "crediti",  titolo: "Crediti / Debiti", icon: CreditCard,  tone: "red" },
  ];

  const totale = (key) => (patr[key] || []).reduce((s, v) => s + Number(v.importo_chf || 0), 0);
  const totAttivi = attivi.reduce((s, c) => s + totale(c.key), 0);
  const totPassivi = passivi.reduce((s, c) => s + totale(c.key), 0);
  const totNetto = totAttivi - totPassivi;

  return (
    <div className="space-y-5" data-testid="dossier-patrimonio-panel">
      {/* Header compatto + bottone salva */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">Patrimonio</h1>
          <p className="text-xs text-stone-500 mt-0.5">Attivi − Passivi = Patrimonio netto</p>
        </div>
        <div className="flex items-center gap-2">
          {patrSaveState === "pending" && (
            <span className="text-[11px] text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Aggiornamento…</span>
          )}
          {patrSaveState === "saved" && (
            <span className="text-[11px] text-emerald-600 font-medium">✓ Aggiornato</span>
          )}
          <Button onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-patrimonio">Salva</Button>
        </div>
      </div>

      {/* KPI sticky in alto: 1 sola riga, 3 numeri grandi */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-card px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Totale Attivi</div>
          <div className="text-2xl font-mono font-bold mt-1 text-stone-900">{fmtCHF(totAttivi)}</div>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Totale Passivi</div>
          <div className="text-2xl font-mono font-bold mt-1 text-red-600">− {fmtCHF(totPassivi)}</div>
        </div>
        <div className="rounded-lg border-2 border-prime bg-prime/5 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-prime font-semibold">Patrimonio Netto</div>
          <div className="text-2xl font-mono font-bold mt-1 text-prime">{fmtCHF(totNetto)}</div>
        </div>
      </div>

      {/* Attivi — grid 2 colonne */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-2 mt-2">Attivi</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {attivi.map((cat) => (
            <PatrimonioCategoria
              key={cat.key}
              cat={cat}
              items={patr[cat.key] || []}
              onChange={(items) => setPatr({ ...patr, [cat.key]: items })}
              tot={totale(cat.key)}
            />
          ))}
        </div>
      </div>

      {/* Passivi — grid 2 colonne */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-red-700 font-semibold mb-2 mt-2">Passivi</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {passivi.map((cat) => (
            <PatrimonioCategoria
              key={cat.key}
              cat={cat}
              items={patr[cat.key] || []}
              onChange={(items) => setPatr({ ...patr, [cat.key]: items })}
              tot={totale(cat.key)}
              isPassivo
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Singola card categoria patrimonio — compatta, collapsible-feel. */
function PatrimonioCategoria({ cat, items, onChange, tot, isPassivo = false }) {
  const addRow = () => onChange([...items, { titolo: "", importo_chf: 0, interessi_percent: 0 }]);
  const updateRow = (i, patch) => {
    const arr = [...items];
    arr[i] = { ...arr[i], ...patch };
    onChange(arr);
  };
  const removeRow = (i) => onChange(items.filter((_, j) => j !== i));
  const totColor = isPassivo ? "text-red-600" : "text-prime";

  // Tone palette → bg-{tone}-100 text-{tone}-700 — Tailwind safelist friendly
  const TONE_CLS = {
    sky:     "bg-sky-100 text-sky-700",
    emerald: "bg-emerald-100 text-emerald-700",
    indigo:  "bg-indigo-100 text-indigo-700",
    orange:  "bg-orange-100 text-orange-700",
    purple:  "bg-purple-100 text-purple-700",
    stone:   "bg-stone-100 text-stone-700",
    rose:    "bg-rose-100 text-rose-700",
    red:     "bg-red-100 text-red-700",
  };
  const Icon = cat.icon;
  const iconCls = TONE_CLS[cat.tone] || TONE_CLS.stone;

  return (
    <div className="rounded-lg border bg-card p-3 hover:shadow-sm transition-shadow" data-testid={`patrimonio-cat-${cat.key}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg ${iconCls} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="text-sm font-semibold text-stone-800">{cat.titolo}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-mono font-bold ${totColor}`}>{fmtCHF(tot)}</span>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={addRow} data-testid={`d-add-${cat.key}`}>
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-stone-400 italic text-center py-2">Nessuna registrazione</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((v, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_auto] gap-1.5 items-center">
              <Input
                value={v.titolo || ""}
                placeholder="Descrizione"
                onChange={(e) => updateRow(i, { titolo: e.target.value })}
                className="h-8 text-xs"
              />
              <NumInput
                value={v.importo_chf}
                onChange={(val) => updateRow(i, { importo_chf: val })}
                className="text-xs"
              />
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-stone-400 hover:text-red-600" onClick={() => removeRow(i)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===========================================================================
 * BUDGET PANEL (Schermata 9)
 * ======================================================================== */
export function BudgetPanel({ dossier, computed, updateSection, saving }) {
  const [bud, setBud] = useState(dossier?.budget || { entrate: [], spese: [], cantone: "ZH" });
  const initialSnapBud = useRef(JSON.stringify(dossier?.budget || {}));
  const lastSavedBud = useRef(JSON.stringify(dossier?.budget || {}));
  const [budSaveState, setBudSaveState] = useState("idle");

  const save = async () => {
    await updateSection("budget", bud);
    lastSavedBud.current = JSON.stringify(bud);
    setBudSaveState("saved");
    toast.success("Budget salvato");
  };

  // Auto-save debounced
  useEffect(() => {
    const cur = JSON.stringify(bud);
    if (cur === initialSnapBud.current) return;
    if (cur === lastSavedBud.current) return;
    setBudSaveState("pending");
    const t = setTimeout(async () => {
      try {
        await updateSection("budget", bud);
        lastSavedBud.current = cur;
        setBudSaveState("saved");
        setTimeout(() => setBudSaveState("idle"), 2000);
      } catch (e) { setBudSaveState("idle"); }
    }, 800);
    return () => clearTimeout(t);
  }, [bud, updateSection]);

  const addEntrata = () => setBud({ ...bud, entrate: [...(bud.entrate || []), { titolo: "", importo_chf: 0, imponibile: true, liquidita: true }] });
  const addSpesa = () => setBud({ ...bud, spese: [...(bud.spese || []), { titolo: "", importo_chf: 0, imponibile: false, liquidita: true }] });

  const totEntrate = (bud.entrate || []).filter((v) => v.liquidita).reduce((s, v) => s + Number(v.importo_chf || 0), 0);
  const totSpese = (bud.spese || []).filter((v) => v.liquidita).reduce((s, v) => s + Number(v.importo_chf || 0), 0);

  return (
    <div className="space-y-6" data-testid="dossier-budget-panel">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">Budget</h1>
          <p className="text-sm text-stone-500 mt-1">
            Entrate · Spese · Imposte auto-calcolate (cantone selezionato).
            <span className="ml-1 text-stone-400 italic">Le voci "Assicurazioni" si sincronizzano automaticamente dai contratti.</span>
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <Field label="Cantone fiscale">
            <Select value={bud.cantone || "ZH"} onValueChange={(v) => setBud({ ...bud, cantone: v })}>
              <SelectTrigger className="h-9 w-44" data-testid="d-cantone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AG">Aargau (AG)</SelectItem>
                <SelectItem value="AI">Appenzell Innerrhoden (AI)</SelectItem>
                <SelectItem value="AR">Appenzell Ausserrhoden (AR)</SelectItem>
                <SelectItem value="BE">Bern (BE)</SelectItem>
                <SelectItem value="BL">Basel-Landschaft (BL)</SelectItem>
                <SelectItem value="BS">Basel-Stadt (BS)</SelectItem>
                <SelectItem value="FR">Fribourg (FR)</SelectItem>
                <SelectItem value="GE">Genève (GE)</SelectItem>
                <SelectItem value="GL">Glarus (GL)</SelectItem>
                <SelectItem value="GR">Graubünden (GR)</SelectItem>
                <SelectItem value="JU">Jura (JU)</SelectItem>
                <SelectItem value="LU">Luzern (LU)</SelectItem>
                <SelectItem value="NE">Neuchâtel (NE)</SelectItem>
                <SelectItem value="NW">Nidwalden (NW)</SelectItem>
                <SelectItem value="OW">Obwalden (OW)</SelectItem>
                <SelectItem value="SG">St. Gallen (SG)</SelectItem>
                <SelectItem value="SH">Schaffhausen (SH)</SelectItem>
                <SelectItem value="SO">Solothurn (SO)</SelectItem>
                <SelectItem value="SZ">Schwyz (SZ)</SelectItem>
                <SelectItem value="TG">Thurgau (TG)</SelectItem>
                <SelectItem value="TI">Ticino (TI)</SelectItem>
                <SelectItem value="UR">Uri (UR)</SelectItem>
                <SelectItem value="VD">Vaud (VD)</SelectItem>
                <SelectItem value="VS">Valais (VS)</SelectItem>
                <SelectItem value="ZG">Zug (ZG)</SelectItem>
                <SelectItem value="ZH">Zürich (ZH)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-center gap-2">
            {budSaveState === "pending" && (
              <span className="text-[11px] text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Aggiornamento…</span>
            )}
            {budSaveState === "saved" && (
              <span className="text-[11px] text-emerald-600 font-medium">✓ Aggiornato</span>
            )}
            <Button onClick={save} disabled={saving} className="bg-prime text-prime-foreground hover:bg-prime/90" data-testid="d-save-budget">Salva</Button>
          </div>
        </div>
      </div>

      {computed?.imposte ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CardBox><div className="text-[10px] uppercase text-stone-500">Entrate (liq.)</div><div className="text-lg font-mono mt-1">{fmtCHF(totEntrate || computed.budget?.entrate_totali)}</div></CardBox>
          <CardBox><div className="text-[10px] uppercase text-stone-500">Spese (liq.)</div><div className="text-lg font-mono text-red-600 mt-1">- {fmtCHF(totSpese)}</div></CardBox>
          <CardBox><div className="text-[10px] uppercase text-stone-500">Imposte tot.</div><div className="text-lg font-mono text-amber-600 mt-1">- {fmtCHF(computed.imposte.imposta_totale_chf)}</div></CardBox>
          <CardBox className="bg-prime/5"><div className="text-[10px] uppercase text-prime font-semibold">Quota risparmio</div><div className="text-lg font-mono text-prime font-semibold mt-1">{fmtCHF(computed.budget?.quota_risparmio || 0)}</div></CardBox>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardBox>
          <SectionTitle action={<Button size="sm" variant="outline" onClick={addEntrata}><Plus className="w-3 h-3 mr-1" />Entrata</Button>}>Entrate</SectionTitle>
          <BudgetTable rows={bud.entrate || []} onChange={(rows) => setBud({ ...bud, entrate: rows })} testidPrefix="d-entrate" />
        </CardBox>
        <CardBox>
          <SectionTitle action={<Button size="sm" variant="outline" onClick={addSpesa}><Plus className="w-3 h-3 mr-1" />Spesa</Button>}>Spese</SectionTitle>
          <BudgetTable rows={bud.spese || []} onChange={(rows) => setBud({ ...bud, spese: rows })} testidPrefix="d-spese" />
        </CardBox>
      </div>
    </div>
  );
}

function BudgetTable({ rows, onChange, testidPrefix }) {
  if (rows.length === 0) return <p className="text-sm text-stone-400 italic">Nessuna voce</p>;
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 text-[10px] uppercase tracking-wider text-stone-400 font-semibold pb-1 border-b">
        <div>Designazione</div>
        <div>I</div>
        <div>L</div>
        <div className="text-right">Importo</div>
        <div></div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
          <Input value={r.titolo || ""} placeholder="Designazione" onChange={(e) => { const arr = [...rows]; arr[i] = { ...r, titolo: e.target.value }; onChange(arr); }} className="h-8 text-xs" />
          <Checkbox checked={!!r.imponibile} onCheckedChange={(v) => { const arr = [...rows]; arr[i] = { ...r, imponibile: !!v }; onChange(arr); }} />
          <Checkbox checked={!!r.liquidita} onCheckedChange={(v) => { const arr = [...rows]; arr[i] = { ...r, liquidita: !!v }; onChange(arr); }} />
          <NumInput value={r.importo_chf} onChange={(v) => { const arr = [...rows]; arr[i] = { ...r, importo_chf: v }; onChange(arr); }} className="w-28" />
          <Button size="sm" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
    </div>
  );
}

/* ===========================================================================
 * CONSULENZA PANEL (Schermata 10 - Riepilogo Esecutivo + Azioni + Barometro)
 * ======================================================================== */
export function ConsulenzaPanel({ dossier, computed, updateSection, saving }) {
  const analisi = computed?.analisi;
  const score = computed?.barometro_score ?? 0;
  const scoreColor = score >= 7 ? "text-emerald-400" : score >= 4 ? "text-amber-400" : "text-red-400";
  const statoLabel = score >= 7 ? "Situazione SOLIDA" : score >= 4 ? "ATTENZIONE — Lacune" : "CRITICO — Azioni urgenti";

  const statoBg = (s) => ({
    verde: "bg-emerald-50 border-emerald-300 text-emerald-900",
    giallo: "bg-amber-50 border-amber-300 text-amber-900",
    rosso: "bg-red-50 border-red-300 text-red-900",
    neutro: "bg-stone-50 border-stone-200 text-stone-700",
  }[s] || "bg-stone-50 border-stone-200");

  const statoBadge = (s) => ({
    verde: "bg-emerald-500", giallo: "bg-amber-500", rosso: "bg-red-500", neutro: "bg-stone-400",
  }[s] || "bg-stone-400");

  if (!analisi) {
    return (
      <div className="space-y-4" data-testid="dossier-consulenza-panel">
        <h1 className="text-2xl font-serif font-semibold text-stone-900">Consulenza</h1>
        <p className="text-sm text-stone-500">Caricamento analisi automatica…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dossier-consulenza-panel">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-stone-900">Riepilogo & Piano d'azione</h1>
        <p className="text-sm text-stone-500 mt-1">Analisi automatica dei dati raccolti — situazione attuale, lacune e azioni concrete.</p>
      </div>

      {/* BAROMETRO HERO + Riepilogo */}
      <div className="rounded-xl bg-gradient-to-r from-stone-900 to-stone-700 text-white overflow-hidden">
        <div className="p-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">Barometro</div>
              <div className={`text-6xl font-mono font-bold ${scoreColor}`} data-testid="d-barometro-score">{score.toFixed(1)}</div>
              <div className="text-stone-300 text-xs">/10</div>
            </div>
            <div className="w-px bg-stone-600 h-20"></div>
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${scoreColor}`}>{statoLabel}</div>
            <p className="text-sm mt-2 leading-relaxed text-stone-100" data-testid="d-riepilogo-intro">{analisi.riepilogo.intro}</p>
          </div>
        </div>
      </div>

      {/* PUNTI CHIAVE */}
      <CardBox data-testid="d-punti-chiave">
        <SectionTitle>Indicatori principali</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          {analisi.riepilogo.punti_chiave.map((p, i) => (
            <div key={i} className="rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700 font-mono">{p}</div>
          ))}
        </div>
      </CardBox>

      {/* TOP 3 AZIONI PRIORITARIE */}
      {analisi.priorita_top3.length > 0 && (
        <CardBox className="border-l-4 border-prime bg-prime/5" data-testid="d-priorita-top3">
          <SectionTitle>🎯 Top 3 azioni prioritarie</SectionTitle>
          <div className="space-y-3">
            {analisi.priorita_top3.map((p, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`d-priorita-${i}`}>
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-prime text-prime-foreground font-bold text-sm flex items-center justify-center">{i + 1}</div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{p.modulo}</div>
                  <div className="text-sm font-semibold text-stone-900">{p.titolo}</div>
                  <div className="text-sm text-stone-700 mt-1">{p.azione}</div>
                </div>
              </div>
            ))}
          </div>
        </CardBox>
      )}

      {/* LACUNE IDENTIFICATE */}
      {analisi.lacune.length > 0 && (
        <CardBox data-testid="d-lacune">
          <SectionTitle>⚠️ Lacune identificate ({analisi.lacune.length})</SectionTitle>
          <div className="space-y-2">
            {analisi.lacune.map((l, i) => (
              <div key={i} className={`rounded border-l-4 ${statoBg(l.gravita)} px-3 py-2`} data-testid={`d-lacuna-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full ${statoBadge(l.gravita)}`}></div>
                  <div className="text-[10px] uppercase tracking-widest font-bold">{l.gravita}</div>
                  <div className="text-sm font-semibold">{l.area}</div>
                  {l.gap_chf > 0 ? <div className="ml-auto font-mono text-xs font-semibold">Gap: {fmtCHF(l.gap_chf)}</div> : null}
                </div>
                <div className="text-xs">{l.descrizione}</div>
              </div>
            ))}
          </div>
        </CardBox>
      )}

      {/* 14 MODULI con stato auto + situazione + azione */}
      <CardBox data-testid="d-moduli">
        <SectionTitle>Moduli di consulenza — Diagnostica completa</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {analisi.moduli.map((m, i) => (
            <div key={m.key} className={`rounded border-l-4 ${statoBg(m.stato)} p-3`} data-testid={`d-modulo-${m.key}`}>
              <div className="flex items-start gap-2">
                <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-1 ${statoBadge(m.stato)}`}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold">{m.titolo}</span>
                    <span className="text-xs font-semibold">— {m.sottotitolo}</span>
                  </div>
                  <div className="text-xs mt-1.5"><span className="text-stone-500 font-medium">Situazione:</span> {m.situazione}</div>
                  <div className="text-xs mt-1"><span className="text-stone-500 font-medium">Azione:</span> <span className="font-semibold">{m.azione}</span></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardBox>
    </div>
  );
}


/* ===========================================================================
 * SPESE & CONTRATTI PANEL — Pagina 3: vista unica contratti + spese
 *  - I contratti generano voci di spesa automaticamente nel Budget
 *  - Tab "Contratti" e "Altre spese" affiancate
 * ======================================================================== */
export function SpeseContrattiPanel({ dossier, computed, updateSection, reimportContratti, saving }) {
  const [contratti, setContratti] = useState(dossier?.contratti || []);
  const [bud, setBud] = useState(dossier?.budget || { entrate: [], spese: [], cantone: "ZH" });
  const [adding, setAdding] = useState(false);
  const [newTipo, setNewTipo] = useState("registrazione_libera");

  // Re-sync local state whenever the dossier prop changes (e.g. after PATCH
  // refreshes contracts and the backend auto-regenerates the budget.spese_auto).
  useEffect(() => {
    setContratti(dossier?.contratti || []);
    setBud(dossier?.budget || { entrate: [], spese: [], cantone: "ZH" });
  }, [dossier?.contratti, dossier?.budget, dossier?.id]);

  const saveContratti = async () => {
    await updateSection("contratti", contratti);
    toast.success("Contratti salvati");
  };
  const saveBudget = async () => {
    await updateSection("budget", bud);
    toast.success("Spese salvate");
  };

  const totSpeseAss = (bud.spese || []).filter((s) => s.categoria === "assicurazioni").reduce((a, s) => a + Number(s.importo_chf || 0), 0);
  const totAltreSpese = (bud.spese || []).filter((s) => s.categoria !== "assicurazioni").reduce((a, s) => a + Number(s.importo_chf || 0), 0);
  const totContratti = contratti.reduce((a, c) => a + Number(c.premio_annuo_chf || 0), 0);

  const addManual = () => {
    setContratti([...contratti, {
      id: `manual-${Date.now()}`, tipo: newTipo,
      titolo: CONTRATTO_LABELS[newTipo] || newTipo,
      compagnia: "", numero_polizza: "", intestatario: "",
      data_inizio: "", data_fine: "",
      premio_mensile_chf: 0, premio_annuo_chf: 0,
      source: "manual_external", attivo: true, extra: {},
    }]);
    setAdding(false);
  };
  const addAltraSpesa = () => setBud({ ...bud, spese: [...(bud.spese || []), { id: `s-${Date.now()}`, categoria: "altre", titolo: "", importo_chf: 0, frequenza: "mensile", imponibile: false, liquidita: true }] });

  return (
    <div className="space-y-6" data-testid="dossier-spese-contratti-panel">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">3 · Spese & Contratti</h1>
          <p className="text-sm text-stone-500 mt-1">Contratti assicurativi attivi e tutte le altre spese del cliente.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={reimportContratti} disabled={saving} data-testid="d-reimport-contratti"><RefreshCw className="w-3 h-3 mr-1" />Re-importa Wertia</Button>
          <Button size="sm" onClick={() => setAdding(!adding)} data-testid="d-add-contratto"><Plus className="w-3 h-3 mr-1" />Contratto esterno</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CardBox className="border-l-4 border-blue-500/60">
          <div className="text-[10px] uppercase text-stone-500 font-semibold">Contratti</div>
          <div className="text-xl font-mono mt-1">{contratti.length}</div>
          <div className="text-xs text-stone-500 mt-1">Premi annui: <span className="font-mono">{fmtCHF(totContratti)}</span></div>
        </CardBox>
        <CardBox className="border-l-4 border-amber-500/60">
          <div className="text-[10px] uppercase text-stone-500 font-semibold">Spese assicurative (auto)</div>
          <div className="text-xl font-mono mt-1">{fmtCHF(totSpeseAss)}</div>
        </CardBox>
        <CardBox className="border-l-4 border-rose-500/60">
          <div className="text-[10px] uppercase text-stone-500 font-semibold">Altre spese</div>
          <div className="text-xl font-mono mt-1">{fmtCHF(totAltreSpese)}</div>
        </CardBox>
      </div>

      {adding && (
        <CardBox className="border-prime">
          <SectionTitle>Nuovo contratto esterno</SectionTitle>
          <div className="flex gap-2 items-end">
            <Field label="Tipo">
              <Select value={newTipo} onValueChange={setNewTipo}>
                <SelectTrigger className="h-9 w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTRATTO_TIPI_FORM.map((t) => <SelectItem key={t} value={t}>{CONTRATTO_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Button onClick={addManual} className="bg-prime text-prime-foreground" data-testid="d-confirm-add-contratto">Aggiungi</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Annulla</Button>
          </div>
        </CardBox>
      )}

      <CardBox data-testid="d-contratti-list">
        <SectionTitle action={<Button size="sm" onClick={saveContratti} disabled={saving} className="bg-prime text-prime-foreground">Salva contratti</Button>}>Contratti ({contratti.length})</SectionTitle>
        {contratti.length === 0 ? <p className="text-sm text-stone-400 italic">Nessun contratto. Importa da Wertia o aggiungi manualmente.</p> :
          <div className="space-y-2">
            {contratti.map((c, i) => (
              <div key={c.id || i} className="grid grid-cols-[1fr_1fr_1fr_auto_auto_auto] gap-2 items-center border-b border-stone-100 pb-2" data-testid={`d-contratto-${i}`}>
                <div className="text-sm">
                  <div className="font-semibold">{CONTRATTO_LABELS[c.tipo] || c.tipo}</div>
                  <Badge variant={c.source === "manual_external" ? "outline" : "default"} className="mt-0.5 text-[9px]">
                    {c.source === "wertia_policy" ? "Polizza Wertia" : c.source === "wertia_antrag" ? "Antrag Wertia" : "Esterno"}
                  </Badge>
                </div>
                <Input value={c.compagnia || ""} onChange={(e) => { const arr = [...contratti]; arr[i] = { ...c, compagnia: e.target.value }; setContratti(arr); }} className="h-8 text-sm" placeholder="Compagnia" disabled={c.source !== "manual_external"} />
                <Input value={c.numero_polizza || ""} onChange={(e) => { const arr = [...contratti]; arr[i] = { ...c, numero_polizza: e.target.value }; setContratti(arr); }} className="h-8 text-sm" placeholder="N. polizza" disabled={c.source !== "manual_external"} />
                <div className="text-[10px] text-stone-400 uppercase">CHF/anno</div>
                <NumInput value={c.premio_annuo_chf} onChange={(v) => { const arr = [...contratti]; arr[i] = { ...c, premio_annuo_chf: v }; setContratti(arr); }} className="w-32" />
                <Button size="sm" variant="ghost" onClick={() => setContratti(contratti.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
          </div>
        }
      </CardBox>

      <CardBox data-testid="d-altre-spese">
        <SectionTitle action={
          <div className="flex gap-2">
            <Button size="sm" onClick={addAltraSpesa} variant="outline" data-testid="d-add-altra-spesa">
              <Plus className="w-3 h-3 mr-1" />Altra spesa
            </Button>
            <Button size="sm" onClick={saveBudget} disabled={saving} className="bg-prime text-prime-foreground">
              Salva spese
            </Button>
          </div>
        }>Altre spese (non assicurative)</SectionTitle>
        {(bud.spese || []).filter((s) => s.categoria !== "assicurazioni").length === 0 ? <p className="text-sm text-stone-400 italic">Nessuna altra spesa. Aggiungi: locazione, costo della vita, vacanze, ecc.</p> :
          <div className="space-y-2">
            <div className="hidden md:grid grid-cols-[1.2fr_0.9fr_0.9fr_1fr_auto] gap-2 px-1 text-[10px] uppercase tracking-wider text-stone-400 font-semibold">
              <div>Categoria</div>
              <div>Importo</div>
              <div>Frequenza</div>
              <div className="text-right pr-10">CHF/anno</div>
              <div />
            </div>
            {(bud.spese || []).map((s, idx) => {
              if (s.categoria === "assicurazioni") return null;
              const freqMult = { mensile: 12, trimestrale: 4, semestrale: 2, annuale: 1, una_tantum: 1 };
              const fq = s.frequenza || "annuale";
              const periodicVal = fq === "annuale" || fq === "una_tantum"
                ? (s.importo_chf || 0)
                : (s.importo_chf || 0) / (freqMult[fq] || 1);
              const annualVal = (periodicVal || 0) * (freqMult[fq] || 1);
              return (
                <div key={s.id || idx} className="grid grid-cols-[1.2fr_0.9fr_0.9fr_1fr_auto] gap-2 items-center" data-testid={`d-spesa-${idx}`}>
                  <Select value={s.categoria || "altre"} onValueChange={(v) => { const arr = [...bud.spese]; arr[idx] = { ...s, categoria: v }; setBud({ ...bud, spese: arr }); }}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`d-spesa-${idx}-categoria`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="abitare">Abitare</SelectItem>
                      <SelectItem value="costo_vita">Costo della vita</SelectItem>
                      <SelectItem value="trasporti">Trasporti</SelectItem>
                      <SelectItem value="costi_sanitari">Costi sanitari</SelectItem>
                      <SelectItem value="prestiti">Prestiti</SelectItem>
                      <SelectItem value="vacanze">Vacanze</SelectItem>
                      <SelectItem value="altre">Altre</SelectItem>
                    </SelectContent>
                  </Select>
                  <NumInput
                    value={periodicVal}
                    onChange={(v) => {
                      const newAnnual = (Number(v) || 0) * (freqMult[fq] || 1);
                      const arr = [...bud.spese];
                      arr[idx] = { ...s, importo_chf: newAnnual };
                      setBud({ ...bud, spese: arr });
                    }}
                    data-testid={`d-spesa-${idx}-importo`}
                  />
                  <Select
                    value={fq}
                    onValueChange={(v) => {
                      const newAnnual = (periodicVal || 0) * (freqMult[v] || 1);
                      const arr = [...bud.spese];
                      arr[idx] = { ...s, frequenza: v, importo_chf: newAnnual };
                      setBud({ ...bud, spese: arr });
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid={`d-spesa-${idx}-frequenza`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensile">Mensile (×12)</SelectItem>
                      <SelectItem value="trimestrale">Trimestrale (×4)</SelectItem>
                      <SelectItem value="semestrale">Semestrale (×2)</SelectItem>
                      <SelectItem value="annuale">Annuale (×1)</SelectItem>
                      <SelectItem value="una_tantum">Una tantum</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-right font-mono text-sm pr-2 text-stone-700" data-testid={`d-spesa-${idx}-annuale`}>
                    {fmtCHF(annualVal)}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setBud({ ...bud, spese: bud.spese.filter((_, j) => j !== idx) })}><Trash2 className="w-3 h-3" /></Button>
                </div>
              );
            })}
          </div>
        }
      </CardBox>
    </div>
  );
}

/* ===========================================================================
 * RIEPILOGO PANEL — Pagina 5: situazione del cliente in chiaro
 * ======================================================================== */
export function RiepilogoPanel({ dossier, computed }) {
  const analisi = computed?.analisi;
  const score = computed?.barometro_score ?? 0;

  if (!analisi) {
    return (
      <div className="space-y-4" data-testid="dossier-riepilogo-panel">
        <h1 className="text-2xl font-serif font-semibold text-stone-900">5 · Riepilogo & Situazione</h1>
        <p className="text-sm text-stone-500">Caricamento analisi automatica…</p>
      </div>
    );
  }

  // Dati per donut "Dove va il netto annuo"
  const netto = computed?.reddito?.netto_annuo || 0;
  const imposte = computed?.imposte?.imposta_totale_chf || 0;
  const speseAss = analisi?.riepilogo?.spese_assicurative_annue || 0;
  const risparmio = Math.max(0, computed?.budget?.quota_risparmio || 0);
  // Spese vita = netto - imposte - assicurazioni - risparmio (ciò che resta)
  const speseVita = Math.max(0, netto - imposte - speseAss - risparmio);

  const donutData = [
    { label: "Spese di vita", value: speseVita, color: CHART_COLOR.STONE },     // stone neutro (#433E3D)
    { label: "Imposte", value: imposte, color: CHART_COLOR.ERROR },             // rosso (#FF5757)
    { label: "Assicurazioni", value: speseAss, color: CHART_COLOR.GOLD },       // oro (#D7A93F)
    { label: "Risparmio", value: risparmio, color: CHART_COLOR.SUCCESS },       // verde (#389D6D)
  ].filter((d) => d.value > 0);

  // Numeri chiave per KPI card (struttura visuale)
  const kpi = [
    {
      icon: Wallet, label: "Reddito netto", value: fmtCHF(netto),
      sub: netto > 0 ? `${fmtCHF(netto / 12)} / mese` : null,
      tone: "neutral",
    },
    {
      icon: Landmark, label: "AVS vecchiaia", value: fmtCHF(computed?.avs?.rendita_vecchiaia_annua || 0),
      sub: computed?.avs?.anni_mancanti > 0 ? `${computed.avs.anni_mancanti} anni mancanti` : "Scala 44 completa",
      tone: computed?.avs?.anni_mancanti > 5 ? "warn" : "good",
    },
    {
      icon: Building, label: "LPP vecchiaia", value: fmtCHF(computed?.lpp?.rendita_vecchiaia_annua || 0),
      sub: `Cap. 65: ${fmtCHF(computed?.lpp?.capitale_proiettato_a_65 || 0)}`,
      tone: "neutral",
    },
    {
      icon: Receipt, label: "Imposte annue", value: fmtCHF(imposte),
      sub: `Aliq. marg. ${(computed?.imposte?.aliquota_marginale_chf || computed?.imposte?.aliquota_marginale_effettiva || 0).toFixed(1)}%${computed?.imposte?.regime === "quellensteuer" ? " · alla fonte" : ""}`,
      tone: "neutral",
    },
    {
      icon: PiggyBank, label: "Patrimonio netto", value: fmtCHF(computed?.patrimonio?.totale_netto || 0),
      sub: null, tone: "neutral",
    },
    {
      icon: Shield, label: "Spese assicurative", value: fmtCHF(speseAss),
      sub: netto > 0 ? `${(speseAss / netto * 100).toFixed(1)}% del netto` : null,
      tone: speseAss / Math.max(netto, 1) > 0.15 ? "warn" : "good",
    },
    {
      icon: TrendingUp, label: "Quota risparmio", value: fmtCHF(risparmio),
      sub: netto > 0 ? `${(risparmio / netto * 100).toFixed(1)}% del netto` : null,
      tone: risparmio / Math.max(netto, 1) >= 0.10 ? "good" : "warn",
    },
    {
      icon: Target, label: "Pensione vs target",
      value: `${(analisi?.riepilogo?.copertura_pensione_percent || 0).toFixed(0)}%`,
      sub: (analisi?.riepilogo?.gap_pensione_annuo || 0) > 0
        ? `Gap: ${fmtCHF(analisi.riepilogo.gap_pensione_annuo)} / anno`
        : "Target 70% raggiunto",
      tone: (analisi?.riepilogo?.copertura_pensione_percent || 0) >= 70 ? "good" : "warn",
    },
  ];

  const toneClasses = {
    good: "border-emerald-200 bg-emerald-50/60",
    warn: "border-amber-200 bg-amber-50/60",
    bad: "border-red-200 bg-red-50/60",
    neutral: "border-stone-200 bg-white",
  };
  const iconToneClasses = {
    good: "bg-emerald-100 text-emerald-700",
    warn: "bg-amber-100 text-amber-700",
    bad: "bg-red-100 text-red-700",
    neutral: "bg-stone-100 text-stone-700",
  };

  return (
    <div className="space-y-6" data-testid="dossier-riepilogo-panel">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-stone-900">5 · Riepilogo & Situazione</h1>
        <p className="text-sm text-stone-500 mt-1">Cos'ha questo cliente, dove sta, cosa gli serve.</p>
      </div>

      {/* HERO: Gauge + Donut side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4">
        {/* Gauge barometro */}
        <CardBox className="flex flex-col items-center justify-center min-w-[300px]" data-testid="d-barometro-gauge">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Barometro finanziario</div>
          <BarometerGauge value={score} size={260} />
          <div className="text-[10px] text-stone-400 italic mt-2 text-center">
            Score calcolato sui 14 moduli di consulenza
          </div>
        </CardBox>

        {/* Donut distribuzione netto */}
        <CardBox data-testid="d-donut-netto">
          <SectionTitle>Dove va il netto annuo</SectionTitle>
          <DonutDistribuzione data={donutData} total={netto} size={220} />
        </CardBox>
      </div>

      {/* KPI VISUAL GRID — 4 colonne × 2 righe */}
      <div>
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-3">Numeri chiave</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi.map((k, i) => {
            const Icon = k.icon;
            return (
              <div
                key={i}
                className={`rounded-lg border ${toneClasses[k.tone]} p-3 transition-all hover:shadow-sm`}
                data-testid={`d-kpi-${i}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`flex-shrink-0 w-9 h-9 rounded-md ${iconToneClasses[k.tone]} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold truncate">{k.label}</div>
                    <div className="text-base font-mono font-bold text-stone-900 leading-tight truncate">{k.value}</div>
                    {k.sub && <div className="text-[10px] text-stone-500 mt-0.5 leading-tight">{k.sub}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* LACUNE — compatta, una riga ciascuna */}
      {analisi.lacune.length > 0 && (
        <CardBox data-testid="d-riepilogo-lacune">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-stone-700 font-semibold">
              Cosa non va — {analisi.lacune.length} {analisi.lacune.length === 1 ? "lacuna" : "lacune"}
            </h2>
          </div>
          <div className="space-y-1.5">
            {analisi.lacune.map((l, i) => {
              const tone = {
                rosso: { bar: "bg-red-500", chip: "bg-red-100 text-red-700", text: "text-red-700" },
                giallo: { bar: "bg-amber-500", chip: "bg-amber-100 text-amber-700", text: "text-amber-700" },
                verde: { bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700", text: "text-emerald-700" },
              }[l.gravita] || { bar: "bg-stone-400", chip: "bg-stone-100 text-stone-700", text: "text-stone-700" };
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-md border border-stone-200 bg-white pl-0 pr-3 py-2 overflow-hidden"
                  data-testid={`d-lacuna-${i}`}
                >
                  <div className={`w-1.5 self-stretch ${tone.bar}`}></div>
                  <span className={`text-[9px] uppercase tracking-widest font-bold ${tone.chip} px-1.5 py-0.5 rounded`}>
                    {l.gravita}
                  </span>
                  <span className="text-sm font-semibold text-stone-900 flex-shrink-0">{l.area}</span>
                  <span className="text-xs text-stone-600 truncate flex-1 min-w-0">{l.descrizione}</span>
                  {l.gap_chf > 0 && (
                    <span className={`font-mono text-xs font-bold ${tone.text} flex-shrink-0`}>
                      − {fmtCHF(l.gap_chf)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </CardBox>
      )}
    </div>
  );
}

/* ===========================================================================
 * COPERTURE PANEL — Pagina 6: Infortunio · Malattia · Decesso
 * ======================================================================== */
export function CoperturePanel({ dossier, computed }) {
  const cop = computed?.coperture;
  if (!cop) {
    return (
      <div className="space-y-4" data-testid="dossier-coperture-panel">
        <h1 className="text-2xl font-serif font-semibold text-stone-900">6 · Coperture & Lacune</h1>
        <p className="text-sm text-stone-500">Caricamento analisi coperture…</p>
      </div>
    );
  }

  const invInfortunio = cop.scenari.find(s => s.key === "infortunio_invalidita");
  const invMalattia = cop.scenari.find(s => s.key === "malattia_invalidita");
  const morteInfortunio = cop.scenari.find(s => s.key === "decesso_infortunio");
  const morteMalattia = cop.scenari.find(s => s.key === "decesso_malattia");

  return (
    <div className="space-y-6" data-testid="dossier-coperture-panel">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-stone-900">6 · Coperture & Lacune</h1>
          <p className="text-sm text-stone-500 mt-1">
            Cosa percepisce il cliente in 4 scenari critici: <span className="font-semibold">Invalidità</span> (Infortunio · Malattia) · <span className="font-semibold">Decesso</span> (Infortunio · Malattia).
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <KpiMini label="Salario lordo" value={`${fmtCHF(cop.salario_lordo)}`} />
          <KpiMini label="Salario UVG" value={`${fmtCHF(cop.salario_uvg_assicurato)}`} sub={cop.uvg_attivo ? "✓ UVG attivo" : "✗ UVG NO"} />
          <KpiMini label="Famiglia" value={cop.is_sposato ? "Sposato" : "Single"} sub={`${cop.n_figli} figli`} />
        </div>
      </div>

      {/* ========================================================== */}
      {/* INVALIDITÀ */}
      {/* ========================================================== */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-6 bg-amber-500"></div>
          <h2 className="text-lg font-semibold text-stone-900">A · INVALIDITÀ</h2>
          <span className="text-xs text-stone-500">cosa percepisce mensilmente se non può più lavorare</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ScenarioCard
            scenario={invInfortunio}
            tipo="invalidita"
            isInfortunio={true}
            salarioLordo={cop.salario_lordo}
            salarioUvg={cop.salario_uvg_assicurato}
          />
          <ScenarioCard
            scenario={invMalattia}
            tipo="invalidita"
            isInfortunio={false}
            salarioLordo={cop.salario_lordo}
            salarioUvg={cop.salario_uvg_assicurato}
          />
        </div>
      </div>

      {/* ========================================================== */}
      {/* MORTE */}
      {/* ========================================================== */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-6 bg-red-500"></div>
          <h2 className="text-lg font-semibold text-stone-900">B · DECESSO</h2>
          <span className="text-xs text-stone-500">rendite per superstiti (vedova/o · orfani)</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ScenarioCard
            scenario={morteInfortunio}
            tipo="morte"
            isInfortunio={true}
            isSposato={cop.is_sposato}
            nFigli={cop.n_figli}
            salarioLordo={cop.salario_lordo}
            salarioUvg={cop.salario_uvg_assicurato}
          />
          <ScenarioCard
            scenario={morteMalattia}
            tipo="morte"
            isInfortunio={false}
            isSposato={cop.is_sposato}
            nFigli={cop.n_figli}
            salarioLordo={cop.salario_lordo}
            salarioUvg={cop.salario_uvg_assicurato}
          />
        </div>
      </div>
    </div>
  );
}

/** KPI mini compatto */
function KpiMini({ label, value, sub }) {
  return (
    <div className="rounded border bg-card px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="text-sm font-mono font-semibold text-stone-900 leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-stone-500 leading-tight">{sub}</div>}
    </div>
  );
}

/** Card scenario singolo — Timeline + composizione + gap.
 *  tipo: "invalidita" | "morte"
 *  isInfortunio: true (UVG attivo) | false (KTG + LPP)
 */
function ScenarioCard({ scenario: s, tipo, isInfortunio, isSposato, nFigli, salarioLordo, salarioUvg }) {
  if (!s) return null;

  const colorBadge = s.stato === "verde" ? "bg-emerald-500" : s.stato === "giallo" ? "bg-amber-500" : s.stato === "rosso" ? "bg-red-500" : "bg-stone-400";
  const colorText = s.stato === "verde" ? "text-emerald-700" : s.stato === "giallo" ? "text-amber-700" : s.stato === "rosso" ? "text-red-700" : "text-stone-500";

  return (
    <div className="rounded-lg border bg-card p-4" data-testid={`d-scenario-${s.key}`}>
      {/* Header scenario */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${colorBadge}`}></div>
          <div>
            <h3 className="text-base font-semibold text-stone-900">{s.titolo}</h3>
            <p className="text-xs text-stone-500">{s.descrizione}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-mono font-bold ${colorText}`}>{s.copertura_percent.toFixed(0)}%</div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wider">copertura</div>
        </div>
      </div>

      {/* Timeline (solo invalidità: 3 fasi temporali, stile SVAG) */}
      {tipo === "invalidita" && (
        <div className="my-2">
          <TimelineInvaliditaSVAG
            isInfortunio={isInfortunio}
            salarioLordo={salarioLordo}
            salarioUvgAssicurato={salarioUvg}
            fabbisognoAnnuo={s.fabbisogno_annuo}
            coperturaFase3Annua={s.totale_copertura_annua}
            height={280}
          />
        </div>
      )}

      {/* Composizione rendite (per morte: timeline orizzontale stile SVAG) */}
      {tipo === "morte" && (
        <div className="my-2">
          <TimelineDecessoSVAG
            voci={s.voci}
            fabbisognoAnnuo={s.fabbisogno_annuo}
            salarioLordo={salarioLordo}
            isInfortunio={isInfortunio}
            height={260}
          />
        </div>
      )}

      {/* Riassunto: voci + target + gap */}
      <div className="space-y-1 mt-3 text-xs">
        {s.voci.map((v, i) => (
          <div key={i} className="flex justify-between">
            <span className="text-stone-600 truncate">{v.label}</span>
            <span className="font-mono text-stone-900">{fmtCHF(v.importo_chf)}</span>
          </div>
        ))}
        <div className="border-t pt-1.5 mt-1.5 flex justify-between font-semibold">
          <span className="text-stone-900">Totale copertura/anno</span>
          <span className="font-mono text-stone-900">{fmtCHF(s.totale_copertura_annua)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-600">Fabbisogno (80% netto)</span>
          <span className="font-mono text-stone-700">{fmtCHF(s.fabbisogno_annuo)}</span>
        </div>
        {s.gap_annuo > 0 && (
          <div className="flex justify-between bg-red-50 border border-red-200 px-2 py-1 rounded">
            <span className="text-red-700 font-semibold">▼ LACUNA</span>
            <span className="font-mono font-bold text-red-700">− {fmtCHF(s.gap_annuo)}/anno</span>
          </div>
        )}
      </div>

      {/* Raccomandazione Wertia */}
      <div className="mt-3 text-[11px] bg-prime/5 border-l-2 border-prime px-2 py-1.5 rounded">
        <span className="font-bold uppercase text-prime text-[9px] tracking-widest">Consiglio: </span>
        <span className="text-stone-800">{s.raccomandazione}</span>
      </div>
    </div>
  );
}

/** Timeline invalidità + Composizione decesso: sostituiti dai grafici SVAG
 *  in `./DossierCharts.jsx` (TimelineInvaliditaSVAG / TimelineDecessoSVAG). */

/* ===========================================================================
 * STRATEGIA PANEL — Pagina 7: piano d'azione con priorità
 * ======================================================================== */
/* ===========================================================================
 * STRATEGIA & CONSIGLI — Action Plan stile consulenza light premium
 * ======================================================================== */

/** Mappa modulo → icona Lucide moderna */
const MODULO_ICON = {
  pensionamento: Crown,
  patrimonio: Gem,
  budget: PiggyBank,
  imposte: Receipt,
  polizze: Shield,
  rischi: Shield,
  alloggio: Home,
  formazione: GraduationCap,
  basi: User,
  reddito: Wallet,
  liquidita: Wallet,
};

const getModuloIcon = (modKey = "") => {
  const k = (modKey || "").toLowerCase();
  for (const key of Object.keys(MODULO_ICON)) {
    if (k.includes(key)) return MODULO_ICON[key];
  }
  return Target;
};

/** Mappa stato → palette light per la diagnostica */
const STATO_PALETTE = {
  verde:  { dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700",  label: "OK",         bar: "bg-emerald-500" },
  giallo: { dot: "bg-amber-500",   chip: "bg-amber-100 text-amber-700",      label: "Attenzione", bar: "bg-amber-500" },
  rosso:  { dot: "bg-red-500",     chip: "bg-red-100 text-red-700",          label: "Critico",    bar: "bg-red-500" },
  neutro: { dot: "bg-stone-400",   chip: "bg-stone-100 text-stone-700",      label: "N/A",        bar: "bg-stone-400" },
};

/** Card grande per una singola azione TOP-3 — light premium */
function PrioritaCard({ priorita, index }) {
  // 3 palette colorate moderne: oro · ambra/orange · emerald
  const palettes = [
    {
      icon:  "bg-gradient-to-br from-amber-400 to-amber-600",
      ring:  "ring-amber-200",
      hover: "hover:ring-amber-300 hover:shadow-amber-200/50",
      accent: "text-amber-700",
      cta:   "bg-amber-500 hover:bg-amber-600 text-white",
      glow:  "before:from-amber-100/60",
    },
    {
      icon:  "bg-gradient-to-br from-orange-400 to-rose-500",
      ring:  "ring-rose-200",
      hover: "hover:ring-rose-300 hover:shadow-rose-200/50",
      accent: "text-rose-700",
      cta:   "bg-rose-500 hover:bg-rose-600 text-white",
      glow:  "before:from-rose-100/60",
    },
    {
      icon:  "bg-gradient-to-br from-emerald-400 to-teal-500",
      ring:  "ring-emerald-200",
      hover: "hover:ring-emerald-300 hover:shadow-emerald-200/50",
      accent: "text-emerald-700",
      cta:   "bg-emerald-500 hover:bg-emerald-600 text-white",
      glow:  "before:from-emerald-100/60",
    },
  ];
  const p = palettes[index] || palettes[0];
  const Icon = getModuloIcon(priorita.modulo);

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-white ring-2 ${p.ring} ${p.hover} shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 before:absolute before:inset-0 before:bg-gradient-to-br ${p.glow} before:to-transparent before:opacity-50 before:rounded-2xl before:pointer-events-none`}
      data-testid={`d-priorita-${index}`}
    >
      <div className="relative p-5 flex flex-col h-full min-h-[230px]">
        {/* Header: icona grande + numero badge */}
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-2xl ${p.icon} flex items-center justify-center shadow-lg ring-1 ring-white/30`}>
            <Icon className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <div className={`text-xs font-mono font-bold ${p.accent} bg-white border border-current/20 rounded-full px-2.5 py-1 inline-flex items-center gap-1`}>
            <Sparkles className="w-3 h-3" />
            Top {index + 1}
          </div>
        </div>

        {/* Categoria modulo */}
        <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${p.accent} mb-1`}>
          {priorita.modulo}
        </div>

        {/* Titolo + azione */}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-stone-900 leading-tight mb-2">
            {priorita.titolo}
          </h3>
          <p className="text-sm text-stone-600 leading-relaxed">
            {priorita.azione}
          </p>
        </div>

        {/* CTA pulsante moderno */}
        <button
          type="button"
          className={`mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${p.cta} shadow group-hover:shadow-lg group-hover:gap-2`}
        >
          Pianifica intervento
          <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/** Riga compatta per ogni modulo della diagnostica */
function ModuloRow({ m }) {
  const p = STATO_PALETTE[m.stato] || STATO_PALETTE.neutro;
  const Icon = getModuloIcon(m.titolo || m.key);
  return (
    <div
      className="group relative flex items-stretch gap-3 rounded-xl border border-stone-200 bg-white hover:bg-stone-50/60 hover:border-stone-300 hover:shadow-sm transition-all overflow-hidden"
      data-testid={`d-modulo-${m.key}`}
    >
      {/* Barra colorata laterale a tutta altezza */}
      <div className={`w-1.5 ${p.bar} flex-shrink-0`}></div>

      {/* Icona del modulo (sinistra) */}
      <div className="flex-shrink-0 flex items-center pl-1">
        <div className={`w-9 h-9 rounded-lg ${p.chip} flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>

      <div className="flex-1 px-1 py-3 min-w-0">
        {/* Header riga: stato + titolo + sottotitolo */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold rounded ${p.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`}></span>
            {p.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{m.titolo}</span>
          <span className="text-xs text-stone-700 font-medium truncate">— {m.sottotitolo}</span>
        </div>

        {/* Situazione + azione */}
        <div className="space-y-0.5 text-[11px]">
          <div className="text-stone-600">
            <span className="text-stone-400 font-semibold mr-1">SITUAZIONE</span>
            <span>{m.situazione}</span>
          </div>
          <div className="text-stone-900">
            <span className="text-stone-400 font-semibold mr-1">AZIONE</span>
            <span className="font-semibold">{m.azione}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StrategiaPanel({ dossier, computed }) {
  const analisi = computed?.analisi;
  const [filtroStato, setFiltroStato] = React.useState("all"); // all/rosso/giallo/verde
  const score = computed?.barometro_score ?? 0;

  if (!analisi) {
    return (
      <div className="space-y-4" data-testid="dossier-strategia-panel">
        <h1 className="text-2xl font-serif font-semibold text-stone-900">7 · Strategia & Consigli</h1>
        <p className="text-sm text-stone-500">Caricamento…</p>
      </div>
    );
  }

  // Stima score "after"
  const lacuneRosse = analisi.lacune.filter((l) => l.gravita === "rosso").length;
  const lacuneGialle = analisi.lacune.filter((l) => l.gravita === "giallo").length;
  const scoreAfter = Math.min(10, score + lacuneRosse * 0.6 + lacuneGialle * 0.3);
  const deltaScore = scoreAfter - score;
  const deltaPct = score > 0 ? ((deltaScore / score) * 100) : 0;

  // Conteggi per filtri
  const counts = {
    all: analisi.moduli.length,
    rosso: analisi.moduli.filter((m) => m.stato === "rosso").length,
    giallo: analisi.moduli.filter((m) => m.stato === "giallo").length,
    verde: analisi.moduli.filter((m) => m.stato === "verde").length,
  };

  // Ordina moduli per gravità
  const ordineStato = { rosso: 0, giallo: 1, neutro: 2, verde: 3 };
  const moduliOrdinati = [...analisi.moduli].sort(
    (a, b) => (ordineStato[a.stato] ?? 99) - (ordineStato[b.stato] ?? 99)
  );
  const moduliFiltrati = filtroStato === "all"
    ? moduliOrdinati
    : moduliOrdinati.filter((m) => m.stato === filtroStato);

  const filtroBtnCls = (active) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
      active
        ? "bg-stone-900 text-amber-300 shadow"
        : "bg-white border border-stone-300 text-stone-700 hover:border-stone-500 hover:text-stone-900"
    }`;

  return (
    <div className="space-y-6" data-testid="dossier-strategia-panel">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-stone-900">7 · Strategia & Consigli</h1>
        <p className="text-sm text-stone-500 mt-1">Cosa fare per coprire le lacune — piano d'azione concreto.</p>
      </div>

      {/* HERO — light premium con score before/after */}
      <div className="relative rounded-2xl bg-white border border-stone-200 shadow-md overflow-hidden">
        {/* Striscia gradient oro→emerald in alto */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-rose-400 to-emerald-400"></div>

        <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="flex items-start gap-4">
            {/* Icona Rocket pulsante */}
            <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-rose-400 to-emerald-400 items-center justify-center shadow-lg">
              <Rocket className="w-8 h-8 text-white" strokeWidth={2} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.40em] text-amber-600 font-bold mb-2 inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3" />
                Action Plan · {new Date().getFullYear()}
              </div>
              <h2 className="text-2xl md:text-3xl font-serif font-semibold leading-tight text-stone-900">
                {analisi.priorita_top3.length > 0
                  ? <>Implementa le <span className="text-amber-600">{analisi.priorita_top3.length} azioni</span> per portare<br />il tuo cliente da <span className="font-mono">{score.toFixed(1)}</span> a <span className="text-emerald-600 font-mono">{scoreAfter.toFixed(1)}</span> di score</>
                  : <>Situazione finanziaria <span className="text-emerald-600">solida</span> — nessuna azione critica da raccomandare</>
                }
              </h2>
              <p className="text-stone-600 text-sm mt-3 max-w-2xl leading-relaxed">
                {analisi.lacune.length > 0
                  ? `Identificate ${lacuneRosse + lacuneGialle} lacune (${lacuneRosse} critiche, ${lacuneGialle} di attenzione). Sotto trovi il piano d'azione concreto + la diagnostica completa dei 14 moduli di consulenza svizzera.`
                  : "Tutti i 14 moduli di consulenza sono in stato OK. Continua a monitorare annualmente."
                }
              </p>
            </div>
          </div>

          {/* Score before/after */}
          <div className="flex items-center gap-3 md:gap-5 lg:border-l lg:border-stone-200 lg:pl-6">
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-[0.25em] text-stone-500 font-semibold">Oggi</div>
              <div className="text-4xl md:text-5xl font-mono font-bold text-stone-700 leading-tight mt-1">{score.toFixed(1)}</div>
              <div className="text-[10px] text-stone-400 font-mono">/10</div>
            </div>
            <div className="flex flex-col items-center">
              <ArrowUpRight className="w-7 h-7 text-amber-500" strokeWidth={2.5} />
              <div className="text-[10px] font-mono font-bold text-emerald-600 mt-0.5">+{deltaPct.toFixed(0)}%</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-600 font-bold">Dopo azioni</div>
              <div className="text-4xl md:text-5xl font-mono font-bold text-emerald-600 leading-tight mt-1">{scoreAfter.toFixed(1)}</div>
              <div className="text-[10px] text-emerald-400 font-mono">/10</div>
            </div>
          </div>
        </div>
      </div>

      {/* TOP 3 AZIONI PREMIUM */}
      {analisi.priorita_top3.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-amber-600" />
            <h2 className="text-[11px] uppercase tracking-[0.25em] text-stone-700 font-bold">
              Azioni prioritarie raccomandate
            </h2>
            <span className="ml-auto text-[10px] text-stone-400 italic">
              Ordinate per impatto sulla qualità finanziaria
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {analisi.priorita_top3.map((p, i) => (
              <PrioritaCard key={i} priorita={p} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* DIAGNOSTICA 14 MODULI con filtri */}
      <CardBox data-testid="d-diagnostica-14">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Diagnostica completa</h2>
            <p className="text-[10px] text-stone-500 italic mt-0.5">14 moduli di consulenza svizzera ordinati per gravità</p>
          </div>
          {/* Filtri pill */}
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => setFiltroStato("all")} className={filtroBtnCls(filtroStato === "all")} data-testid="d-filtro-all">
              Tutti <span className="font-mono opacity-70">{counts.all}</span>
            </button>
            <button type="button" onClick={() => setFiltroStato("rosso")} className={filtroBtnCls(filtroStato === "rosso")} data-testid="d-filtro-rosso">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Critici <span className="font-mono opacity-70">{counts.rosso}</span>
            </button>
            <button type="button" onClick={() => setFiltroStato("giallo")} className={filtroBtnCls(filtroStato === "giallo")} data-testid="d-filtro-giallo">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Attenzione <span className="font-mono opacity-70">{counts.giallo}</span>
            </button>
            <button type="button" onClick={() => setFiltroStato("verde")} className={filtroBtnCls(filtroStato === "verde")} data-testid="d-filtro-verde">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              OK <span className="font-mono opacity-70">{counts.verde}</span>
            </button>
          </div>
        </div>

        {moduliFiltrati.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            {moduliFiltrati.map((m) => (
              <ModuloRow key={m.key} m={m} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-stone-400 text-sm italic flex flex-col items-center gap-2">
            <CircleCheck className="w-8 h-8 text-stone-300" />
            Nessun modulo in questo stato.
          </div>
        )}
      </CardBox>
    </div>
  );
}

/* ===========================================================================
 * EXPORT PANEL — sezione-per-sezione
 * ======================================================================== */
export function ExportPanel({ dossier }) {
  const [exporting, setExporting] = React.useState(false);

  const doExport = async (fmt, sezione) => {
    if (!dossier?.id) return;
    setExporting(true);
    try {
      const url = sezione ? `${API}/dossiers/${dossier.id}/export.${fmt}?section=${sezione}` : `${API}/dossiers/${dossier.id}/export.${fmt}`;
      const res = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Dossier_${dossier.persone?.[0]?.cognome || "client"}_${sezione || "completo"}.${fmt}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Export ${fmt.toUpperCase()} completato`);
    } catch (e) {
      toast.error(`Export ${fmt.toUpperCase()} fallito`);
    } finally {
      setExporting(false);
    }
  };

  const sezioni = [
    { key: "", label: "Dossier completo", desc: "Tutte le sezioni in un solo file" },
    { key: "anagrafica", label: "1 · Dati cliente", desc: "Anagrafica + indirizzo + figli" },
    { key: "salario", label: "2 · Salario / Entrate", desc: "Reddito + accessori + sostitutivi" },
    { key: "spese_contratti", label: "3 · Spese & Contratti", desc: "Lista contratti + altre spese" },
    { key: "patrimonio", label: "4 · Patrimonio", desc: "Liquidità · Titoli · Crypto · Immobili" },
    { key: "riepilogo", label: "5 · Riepilogo & Situazione", desc: "Score + intro + lacune" },
    { key: "coperture", label: "6 · Coperture & Lacune", desc: "Infortunio · Malattia · Decesso" },
    { key: "strategia", label: "7 · Strategia & Consigli", desc: "Top 3 azioni + diagnostica" },
  ];

  return (
    <div className="space-y-6" data-testid="dossier-export-panel">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-stone-900">Export Excel / PDF</h1>
        <p className="text-sm text-stone-500 mt-1">Scarica il dossier completo o singole sezioni in formato Excel o PDF.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sezioni.map((s) => (
          <CardBox key={s.key} className="border-l-4 border-prime/40" data-testid={`d-export-${s.key || "completo"}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-stone-900">{s.label}</div>
                <div className="text-xs text-stone-500 mt-0.5">{s.desc}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => doExport("xlsx", s.key)} disabled={exporting} data-testid={`d-export-xlsx-${s.key || "completo"}`}>
                  <Download className="w-3 h-3 mr-1" />XLSX
                </Button>
                <Button size="sm" variant="outline" onClick={() => doExport("pdf", s.key)} disabled={exporting} data-testid={`d-export-pdf-${s.key || "completo"}`}>
                  <Download className="w-3 h-3 mr-1" />PDF
                </Button>
              </div>
            </div>
          </CardBox>
        ))}
      </div>
    </div>
  );
}

